import {
  DINING_CHAIR_DEPTH_M,
  DINING_CHAIR_WIDTH_M,
  DINING_ROUND_TABLE_DIAMETER_M,
  diningRectChairXOffsetM,
  diningRoundTableRadiusM,
} from "../../dining/diningLayoutConstants";
import {
  diningChairRowCentersM,
  diningRectFootprintScales,
  diningRoundChairPositionAtAngle,
  diningRoundChairScalesAtAngle,
  diningRoundFootprintScales,
  getDiningRectLayoutForChairCount,
} from "../../dining/diningSharedLayout";
import {
  BookShelfType,
  SmartTableType,
  SvgType,
  SymbolType,
  isBookShelfType,
  isSmartTableType,
  isSvgType,
  isPolygonSymbolType,
} from "../../../../../types/floorplannerTypes";
import { normalizeSmartTableHeight } from "../../dining/smartTableConfig";
import {
  SMART_TABLE_TOP_THICKNESS_M,
  resolveSmartTableLayout,
  resolveSmartTableArmchairProfile,
} from "../../dining/smartTableLayout";
import { resolveBookShelfLayout } from "../../livingRoom/bookShelfLayout";
import {
  PrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DDefaults,
  getPrimitiveSymbol3DModelKeyForSymbol,
  getScaledPrimitiveSymbol3DAuthoredHeight,
  getScaledPrimitiveSymbol3DReferenceHeight,
} from "../../registry/primitiveSymbol3DConfig";
import { FloorPlan3DMaterialSlot } from "../../../materials/materialResolver";
import {
  LOUNGE_CHAIR_1_CUSHION_SVG_OUTLINE,
  LOUNGE_CHAIR_1_SHELL_SVG_OUTLINE,
  LOUNGE_CHAIR_1_SVG_BOUNDS,
} from "./loungeChair1SvgOutlines";
import type { LoungeChair1BackrestProfile } from "./loungeChair1Geometry";
import type { PillowShapeOptions } from "./loungeChairPillowGeometry";
import { createHangersParts } from "./hangersGeometry";
import { createBonnieParts } from "./bonnieGeometry";

export type LivingRoom3DPart =
  | {
      id: string;
      type: "box";
      center: [number, number, number];
      size: [number, number, number];
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      /** Renders the box with cushioned, rounded edges (0 = hard, 1 = softest). */
      softness?: number;
      rotationX?: number;
      rotationY?: number;
      rotationZ?: number;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
      skipSelectionOutline?: boolean;
    }
  | {
      id: string;
      type: "cylinder";
      center: [number, number, number];
      height: number;
      radiusX: number;
      radiusY: number;
      radiusTopScale?: number;
      radiusBottomScale?: number;
      radialSegments: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      /** Keeps the material colour when the symbol carries a custom fill colour. */
      ignoreSymbolFillColor?: boolean;
      rotationX?: number;
      rotationY?: number;
      rotationZ?: number;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
      skipSelectionOutline?: boolean;
    }
  | {
      id: string;
      type: "ellipsoid";
      center: [number, number, number];
      radiusX: number;
      radiusY: number;
      radiusZ: number;
      widthSegments: number;
      heightSegments: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      castShadow?: boolean;
      receiveShadow?: boolean;
      rotationX?: number;
      rotationY?: number;
      rotationZ?: number;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
      skipSelectionOutline?: boolean;
    }
  | {
      id: string;
      type: "bonnieTorso";
      center: [number, number, number];
      width: number;
      depth: number;
      height: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      castShadow?: boolean;
      receiveShadow?: boolean;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
      skipSelectionOutline?: boolean;
    }
  | {
      id: string;
      type: "bonnieProfile";
      center: [number, number, number];
      profile: Array<{
        center: [number, number, number];
        radiusX: number;
        radiusY: number;
      }>;
      radialSegments?: number;
      verticalSegments?: number;
      capStart?: boolean;
      capEnd?: boolean;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      castShadow?: boolean;
      receiveShadow?: boolean;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
      skipSelectionOutline?: boolean;
    }
  | {
      id: string;
      type: "extrudedShape";
      center: [number, number, number];
      points: [number, number][];
      holes?: [number, number][][];
      z: number;
      height: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      rotationX?: number;
      rotationY?: number;
      rotationZ?: number;
      bevelThickness?: number;
      bevelSize?: number;
      bevelSegments?: number;
      curveSegments?: number;
      /** Lowers top vertices toward +Y for a sloping backrest silhouette. */
      topSlopeMaxDrop?: number;
      topSlopeOuterRadius?: number;
      topSlopeInnerRadius?: number;
      backrestProfile?: LoungeChair1BackrestProfile;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
    }
  | {
      id: string;
      type: "pillow";
      center: [number, number, number];
      size: [number, number, number];
      segments?: [number, number, number];
      /** Overrides the pillow puff shape, e.g. for a squarer bed pillow. */
      shape?: PillowShapeOptions;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      rotationX?: number;
      rotationY?: number;
      rotationZ?: number;
      roughness?: number;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
    }
  | {
      id: string;
      type: "loungeChairShell";
      variant?: "loungeChair2";
      center: [number, number, number];
      width: number;
      depth: number;
      height: number;
      armHeight: number;
      shellThickness: number;
      straightSegments?: number;
      backSegments?: number;
      railSegments?: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
    }
  | {
      id: string;
      type: "slopedBasin";
      center: [number, number, number];
      topPoints: [number, number][];
      bottomPoints: [number, number][];
      topZ: number;
      bottomZ: number;
      verticalSegments?: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
    }
  | {
      id: string;
      type: "instancedCylinders";
      instances: Array<{
        center: [number, number, number];
        height: number;
        radiusX: number;
        radiusY: number;
        rotationZ?: number;
      }>;
      radialSegments: number;
      materialSlot: FloorPlan3DMaterialSlot;
      useFillColor?: boolean;
      skipSelectionOutline?: boolean;
      renderOrder?: number;
      polygonOffset?: boolean;
      polygonOffsetFactor?: number;
      depthWrite?: boolean;
    };

export type LivingRoom3DGeometry = {
  modelKey: PrimitiveSymbol3DModelKey;
  parts: LivingRoom3DPart[];
};

const MIN_SIZE = 0.04;
/** Smallest foliage disc size so canopy leaves can render smaller than furniture parts. */
const FOLIAGE_MIN_SIZE = 0.008;
const LEG_SIZE = 0.045;
const DINING_TABLE_SURFACE_HEIGHT = 0.76;
const DINING_TABLE_DEFAULT_MODEL_HEIGHT = 0.78;
const DINING_SEAT_SURFACE_HEIGHT = 0.46;
const DINING_SEAT_THICKNESS = 0.06;
const DINING_CHAIR_LEG_SIZE = 0.028;
const DINING_CHAIR_RAIL_SIZE = 0.022;
/** Standard dining chair footprint: 450 mm wide x 500 mm deep (matches standalone chair symbol). */
const DINING_CHAIR_WIDTH = DINING_CHAIR_WIDTH_M;
const DINING_CHAIR_DEPTH = DINING_CHAIR_DEPTH_M;
const POLYGON_COFFEE_TABLE_MIN_HEIGHT = 0.42;
const KITCHEN_TOE_HEIGHT = 0.12;
const ORGANIC_COFFEE_TABLE_OUTLINE: [number, number][] = [
  [0.0229, 0.4991],
  [0.071, 0.4991],
  [0.2551, 0.4881],
  [0.2811, 0.4844],
  [0.3223, 0.4756],
  [0.3349, 0.4718],
  [0.3412, 0.4699],
  [0.3607, 0.4642],
  [0.419, 0.4354],
  [0.4448, 0.4127],
  [0.4505, 0.4075],
  [0.4634, 0.3967],
  [0.4861, 0.3395],
  [0.4886, 0.3319],
  [0.4952, 0.3114],
  [0.4986, 0.3028],
  [0.4978, 0.2624],
  [0.4959, 0.2425],
  [0.4938, 0.2216],
  [0.4928, 0.211],
  [0.4919, 0.2008],
  [0.491, 0.1914],
  [0.4751, 0.1268],
  [0.4717, 0.1146],
  [0.4684, 0.1029],
  [0.4654, 0.0922],
  [0.4427, 0.038],
  [0.4369, 0.0271],
  [0.4059, -0.0294],
  [0.3771, -0.0826],
  [0.3718, -0.0923],
  [0.3672, -0.1009],
  [0.3364, -0.1438],
  [0.3272, -0.1564],
  [0.3114, -0.177],
  [0.3025, -0.1885],
  [0.2721, -0.2244],
  [0.2563, -0.2418],
  [0.2499, -0.2489],
  [0.2351, -0.2655],
  [0.2078, -0.2908],
  [0.1948, -0.3018],
  [0.188, -0.3075],
  [0.1717, -0.3216],
  [0.1159, -0.3682],
  [0.1078, -0.375],
  [0.0643, -0.4033],
  [0.044, -0.4159],
  [0.0378, -0.4197],
  [0.0221, -0.4305],
  [-0.2108, -0.4998],
  [-0.2219, -0.4998],
  [-0.2325, -0.5],
  [-0.2422, -0.5],
  [-0.2955, -0.4863],
  [-0.3026, -0.4839],
  [-0.3162, -0.4793],
  [-0.3314, -0.4746],
  [-0.3644, -0.4551],
  [-0.3726, -0.4467],
  [-0.3814, -0.4377],
  [-0.3909, -0.4287],
  [-0.4116, -0.4077],
  [-0.4175, -0.403],
  [-0.4339, -0.3714],
  [-0.4499, -0.3331],
  [-0.4553, -0.32],
  [-0.4608, -0.3069],
  [-0.4725, -0.2441],
  [-0.4868, -0.1362],
  [-0.4851, 0.1944],
  [-0.4772, 0.2134],
  [-0.4734, 0.2213],
  [-0.4695, 0.2292],
  [-0.4497, 0.2703],
  [-0.3821, 0.3711],
  [-0.3644, 0.3865],
  [-0.3359, 0.4108],
  [-0.3184, 0.426],
  [-0.3124, 0.431],
  [-0.3015, 0.4411],
  [-0.2463, 0.4694],
  [-0.2255, 0.477],
];

export function isLivingRoom3DSymbol(symbol: SymbolType): boolean {
  return getPrimitiveSymbol3DModelKeyForSymbol(symbol) !== undefined;
}

export function createLivingRoomSymbol3DGeometry(
  symbol: SymbolType,
): LivingRoom3DGeometry | null {
  const modelKey = getPrimitiveSymbol3DModelKeyForSymbol(symbol);
  if (!modelKey) return null;

  if (isSmartTableType(symbol)) {
    return {
      modelKey,
      parts: createSmartTableParts(symbol),
    };
  }

  if (isBookShelfType(symbol)) {
    return {
      modelKey,
      parts: createBookShelfParts(symbol),
    };
  }

  const footprint = getFootprint(symbol, modelKey);
  const height = getHeight(symbol, modelKey);
  const baseElevation = getBaseElevation(symbol, modelKey);

  if (modelKey.includes("sofa")) {
    return {
      modelKey,
      parts: createSofaParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey === "living:armchair") {
    const authoredHeight = getScaledPrimitiveSymbol3DAuthoredHeight(
      modelKey,
      height,
    );
    const seatSurfaceHeight =
      getScaledPrimitiveSymbol3DReferenceHeight(modelKey, height) ??
      authoredHeight * 0.4;
    return {
      modelKey,
      parts: createArmchairParts(
        footprint.width,
        footprint.depth,
        authoredHeight,
        baseElevation,
        seatSurfaceHeight,
      ),
    };
  }

  if (
    modelKey === "living:lounge-chair-1" ||
    modelKey === "living:lounge-chair-2"
  ) {
    return {
      modelKey,
      parts: createLoungeChairParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey === "dining:chair") {
    return {
      modelKey,
      parts: createStandaloneDiningChairParts(
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey === "dining:armchair") {
    return {
      modelKey,
      parts: createDiningArmchairParts(
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.includes("chair") && !modelKey.includes("table")) {
    return {
      modelKey,
      parts: createChairParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.includes("coffee-table")) {
    return {
      modelKey,
      parts: createCoffeeTableParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.includes("screen")) {
    return {
      modelKey,
      parts: createScreenParts(
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("symbols:")) {
    return {
      modelKey,
      parts: createSymbolsParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("botanical:")) {
    return {
      modelKey,
      parts: createBotanicalParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("bedroom:")) {
    return {
      modelKey,
      parts: createBedParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("bathroom:")) {
    return {
      modelKey,
      parts: createBathroomParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("kitchen:")) {
    return {
      modelKey,
      parts: createKitchenParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  if (modelKey.startsWith("dining:")) {
    return {
      modelKey,
      parts: createDiningParts(
        modelKey,
        footprint.width,
        footprint.depth,
        height,
        baseElevation,
      ),
    };
  }

  return {
    modelKey,
    parts: createScreenParts(
      footprint.width,
      footprint.depth,
      height,
      baseElevation,
    ),
  };
}

function createSofaParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const authoredHeight = getScaledPrimitiveSymbol3DAuthoredHeight(
    modelKey,
    height,
  );
  const seatSurfaceHeight =
    getScaledPrimitiveSymbol3DReferenceHeight(modelKey, height) ??
    authoredHeight * 0.4;

  if (modelKey === "living:sofa-modular") {
    return createModularSofaParts(
      width,
      depth,
      authoredHeight,
      baseElevation,
      seatSurfaceHeight,
    );
  }

  if (modelKey === "living:sofa-chaise") {
    return createCurvedChaiseSofaParts(
      width,
      depth,
      authoredHeight,
      baseElevation,
      seatSurfaceHeight,
    );
  }

  const seats = modelKey === "living:sofa-3-seat" ? 3 : 2;
  return createStraightSofaParts(
    width,
    depth,
    authoredHeight,
    baseElevation,
    seats,
    seatSurfaceHeight,
  );
}

function createStraightSofaParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seats: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const seatLift = seatSurfaceHeight - height * 0.4;
  const armWidth = Math.max(width * 0.075, 0.08);
  const armDepth = depth * 0.9;
  const armHeight = height * 0.62 + seatLift;
  const armZ = baseElevation + height * 0.18;
  const backDepth = Math.max(depth * 0.14, MIN_SIZE);
  const backHeight = height * 0.7 + seatLift;
  const backZ = baseElevation + height * 0.2;
  const seatDepth = depth * 0.66;
  const seatHeight = height * 0.16;
  const seatZ = baseElevation + seatSurfaceHeight - seatHeight;
  const cushionGap = Math.min(width * 0.012, 0.022);
  const cushionAreaWidth = width - armWidth * 2.35;
  const cushionWidth = (cushionAreaWidth - cushionGap * (seats - 1)) / seats;
  const cushionStep = cushionWidth + cushionGap;
  const backCushionHeight = height * 0.46;
  const backCushionDepth = depth * 0.1;
  const backCushionZ = baseElevation + height * 0.38 + seatLift;
  const legHeight = height * 0.18;
  const railZ = baseElevation + height * 0.12;
  const railHeight = Math.max(seatZ - railZ + height * 0.01, height * 0.14);

  const parts: LivingRoom3DPart[] = [
    roundedSofaPart(
      "base-rail",
      0,
      -depth * 0.1,
      railZ,
      width * 0.94,
      depth * 0.72,
      railHeight,
      depth * 0.08,
    ),
    roundedSofaPart(
      "back",
      0,
      depth * 0.38,
      backZ,
      width * 0.94,
      backDepth,
      backHeight,
      backDepth * 0.45,
    ),
    roundedSofaPart(
      "left-arm",
      -width / 2 + armWidth / 2,
      -depth * 0.04,
      armZ,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.42,
    ),
    roundedSofaPart(
      "right-arm",
      width / 2 - armWidth / 2,
      -depth * 0.04,
      armZ,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.42,
    ),
    ...createSplayedSofaLegs(
      width * 0.82,
      depth * 0.62,
      baseElevation,
      legHeight,
      0,
      -depth * 0.1,
    ),
  ];

  for (let index = 0; index < seats; index += 1) {
    const x = (index - (seats - 1) / 2) * cushionStep;
    parts.push(
      roundedSofaPart(
        `seat-cushion-${index}`,
        x,
        -depth * 0.08,
        seatZ,
        cushionWidth,
        seatDepth,
        seatHeight,
        Math.min(cushionWidth, seatDepth) * 0.12,
      ),
      roundedSofaPart(
        `back-cushion-${index}`,
        x,
        depth * 0.29,
        backCushionZ,
        cushionWidth,
        backCushionDepth,
        backCushionHeight,
        Math.min(cushionWidth, backCushionHeight) * 0.08,
      ),
    );
  }

  return parts;
}

function createModularSofaParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const seatLift = seatSurfaceHeight - height * 0.41;
  const armWidth = Math.max(width * 0.075, 0.08);
  const backThickness = Math.max(depth * 0.06, MIN_SIZE);
  const mainSeatDepth = depth * 0.34;
  const chaiseWidth = width * 0.31;
  const chaiseCushionDepth = depth * 0.28;
  const seatHeight = height * 0.16;
  const seatZ = baseElevation + seatSurfaceHeight - seatHeight;
  const railZ = baseElevation + height * 0.12;
  const railHeight = Math.max(seatZ - railZ + height * 0.01, height * 0.14);
  const backZ = baseElevation + height * 0.2;
  const backY = depth * 0.4;
  const backHeight = height * 0.68 + seatLift;
  const armHeight = height * 0.58 + seatLift;
  const armZ = baseElevation + height * 0.18;
  const backCushionHeight = height * 0.46;
  const backCushionDepth = depth * 0.08;
  const backCushionZ = baseElevation + height * 0.38 + seatLift;
  const legHeight = height * 0.18;
  const mainSeatY = depth * 0.16;
  const leftInnerX = -width / 2 + armWidth * 1.2;
  const rightInnerX = width / 2 - armWidth * 1.35;
  const chaiseX = rightInnerX - chaiseWidth / 2;
  const mainSeatFrontY = mainSeatY - mainSeatDepth / 2;
  const chaiseSeatY = mainSeatFrontY - chaiseCushionDepth / 2 + depth * 0.006;
  const chaiseSeatStep = chaiseCushionDepth - depth * 0.006;
  const mainCushionCount = 3;
  const mainCushionGap = Math.min(width * 0.012, 0.024);
  const mainCushionAreaWidth =
    chaiseX - chaiseWidth / 2 - leftInnerX - mainCushionGap;
  const mainCushionWidth =
    (mainCushionAreaWidth - mainCushionGap * (mainCushionCount - 1)) /
    mainCushionCount;
  const mainCushionStartX = leftInnerX + mainCushionWidth / 2;
  const backCushionY =
    mainSeatY + mainSeatDepth / 2 + backCushionDepth / 2 - depth * 0.005;
  const sideBackWidth = Math.max(armWidth * 0.64, MIN_SIZE);
  const sideInnerFaceX = width / 2 - armWidth;
  const sideBackX = sideInnerFaceX - sideBackWidth / 2 + sideBackWidth * 0.1;

  const parts: LivingRoom3DPart[] = [
    roundedSofaPart(
      "base-rail-main",
      -width * 0.03,
      mainSeatY,
      railZ,
      width * 0.9,
      mainSeatDepth * 1.12,
      railHeight,
      mainSeatDepth * 0.12,
    ),
    roundedSofaPart(
      "base-rail-chaise",
      chaiseX,
      (mainSeatFrontY + (chaiseSeatY - chaiseSeatStep)) / 2,
      railZ,
      chaiseWidth * 1.14,
      Math.abs(mainSeatFrontY - (chaiseSeatY - chaiseSeatStep)) +
        chaiseCushionDepth,
      railHeight,
      chaiseWidth * 0.1,
    ),
    roundedSofaPart(
      "corner-seat",
      chaiseX,
      mainSeatY,
      seatZ,
      chaiseWidth,
      mainSeatDepth,
      seatHeight,
      chaiseWidth * 0.12,
    ),
    roundedSofaPart(
      "back-main",
      0,
      backY,
      backZ,
      width * 0.94,
      backThickness,
      backHeight,
      backThickness * 0.45,
    ),
    roundedSofaPart(
      "right-side",
      width / 2 - armWidth / 2,
      -depth * 0.1,
      backZ,
      armWidth,
      depth * 0.92,
      backHeight,
      armWidth * 0.45,
    ),
    roundedSofaPart(
      "left-arm",
      -width / 2 + armWidth / 2,
      mainSeatY,
      armZ,
      armWidth,
      mainSeatDepth * 1.22,
      armHeight,
      armWidth * 0.42,
    ),
    ...createModularSofaLegs(width, depth, baseElevation, legHeight),
  ];

  for (let index = 0; index < mainCushionCount; index += 1) {
    const x = mainCushionStartX + index * (mainCushionWidth + mainCushionGap);
    parts.push(
      roundedSofaPart(
        `main-seat-cushion-${index}`,
        x,
        mainSeatY,
        seatZ,
        mainCushionWidth,
        mainSeatDepth,
        seatHeight,
        Math.min(mainCushionWidth, mainSeatDepth) * 0.12,
      ),
      roundedSofaPart(
        `back-cushion-${index}`,
        x,
        backCushionY,
        backCushionZ,
        mainCushionWidth,
        backCushionDepth,
        backCushionHeight,
        Math.min(mainCushionWidth, height) * 0.06,
      ),
    );
  }

  parts.push(
    roundedSofaPart(
      "corner-back-cushion",
      chaiseX,
      backCushionY,
      backCushionZ,
      chaiseWidth,
      backCushionDepth,
      backCushionHeight,
      Math.min(chaiseWidth, backCushionHeight) * 0.06,
    ),
  );

  for (let index = 0; index < 2; index += 1) {
    const y = chaiseSeatY - index * chaiseSeatStep;
    parts.push(
      roundedSofaPart(
        index === 0 ? "chaise-seat" : `chaise-seat-cushion-${index}`,
        chaiseX,
        y,
        seatZ,
        chaiseWidth,
        chaiseCushionDepth,
        seatHeight,
        Math.min(chaiseWidth, chaiseCushionDepth) * 0.12,
      ),
    );
  }

  [mainSeatY, chaiseSeatY, chaiseSeatY - chaiseSeatStep].forEach((y, index) => {
    const cushionDepth = index === 0 ? mainSeatDepth : chaiseCushionDepth;
    parts.push(
      roundedSofaPart(
        `right-back-cushion-${index}`,
        sideBackX,
        y,
        backCushionZ,
        sideBackWidth,
        cushionDepth * 0.9,
        backCushionHeight,
        sideBackWidth * 0.45,
      ),
    );
  });

  return parts;
}

function roundedSofaPart(
  id: string,
  centerX: number,
  centerY: number,
  z: number,
  width: number,
  depth: number,
  height: number,
  radius: number,
): LivingRoom3DPart {
  return extrudedShape(
    id,
    createRoundedRectOutline(
      centerX,
      centerY,
      Math.max(width, MIN_SIZE),
      Math.max(depth, MIN_SIZE),
      radius,
    ),
    z,
    height,
    "furnitureFabric",
    false,
  );
}

function createSplayedSofaLegs(
  width: number,
  depth: number,
  baseElevation: number,
  legHeight: number,
  offsetX = 0,
  offsetY = 0,
): LivingRoom3DPart[] {
  const legSize = Math.max(LEG_SIZE * 0.82, 0.032);
  const x = width / 2;
  const y = depth / 2;
  const z = baseElevation + legHeight / 2;
  const tilt = 0.16;
  const corners = [
    ["front-left", -x, -y, -tilt, tilt],
    ["front-right", x, -y, -tilt, -tilt],
    ["back-left", -x, y, tilt, tilt],
    ["back-right", x, y, tilt, -tilt],
  ] as const;

  return corners.map(([name, localX, localY, rotationX, rotationY]) => ({
    ...box(
      `leg-${name}`,
      [offsetX + localX, offsetY + localY, z],
      [legSize, legSize, legHeight],
      "furnitureMetal",
      false,
      0,
      legSize,
    ),
    rotationX,
    rotationY,
  }));
}

function createModularSofaLegs(
  width: number,
  depth: number,
  baseElevation: number,
  legHeight: number,
): LivingRoom3DPart[] {
  const anchors = [
    ["back-left", -width * 0.38, depth * 0.33],
    ["back-right", width * 0.36, depth * 0.33],
    ["front-left-main", -width * 0.38, -depth * 0.02],
    ["inner-corner", width * 0.12, -depth * 0.04],
    ["inside-elbow", width * 0.12, -depth * 0.18],
    ["front-right-chaise", width * 0.36, -depth * 0.4],
    ["mid-right-chaise", width * 0.36, -depth * 0.08],
  ] as const;
  const legSize = Math.max(LEG_SIZE * 0.82, 0.032);
  const z = baseElevation + legHeight / 2;
  const tilt = 0.14;

  return anchors.map(([name, x, y]) => ({
    ...box(
      `leg-${name}`,
      [x, y, z],
      [legSize, legSize, legHeight],
      "furnitureMetal",
      false,
      0,
      legSize,
    ),
    rotationX: y >= 0 ? tilt : -tilt,
    rotationY: x >= 0 ? -tilt : tilt,
  }));
}

function createCurvedChaiseSofaParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const segmentCount = 9;
  const parts: LivingRoom3DPart[] = [];
  const seatPartHeight = height * 0.24;
  const seatLift = seatSurfaceHeight - height * 0.36;
  const seatZ = baseElevation + seatSurfaceHeight - seatPartHeight / 2;
  const seatBottomZ = seatZ - seatPartHeight / 2;
  const seatPanZ = baseElevation + height * 0.12;
  const seatPanHeight = Math.max(
    seatBottomZ - seatPanZ + height * 0.02,
    height * 0.2,
  );
  const backZ = baseElevation + height * 0.5 + seatLift / 2;
  const legHeight = height * 0.16;
  const legZ = baseElevation + legHeight / 2;

  parts.push(
    extrudedShape(
      "curved-seat-pan",
      createCurvedBandPoints(
        -depth * 0.15,
        depth * 0.14,
        depth * 0.72,
        width * 0.43,
      ),
      seatPanZ,
      seatPanHeight,
      "furnitureFabric",
      true,
    ),
    extrudedShape(
      "curved-back",
      createCurvedBandPoints(
        depth * 0.14,
        depth * 0.25,
        depth * 0.1,
        width * 0.43,
      ),
      baseElevation + height * 0.22,
      height * 0.58 + seatLift,
      "furnitureFabric",
      true,
    ),
    extrudedShape(
      "curved-back-rail",
      createCurvedBandPoints(
        depth * 0.2,
        depth * 0.27,
        depth * 0.07,
        width * 0.43,
      ),
      baseElevation + height * 0.72 + seatLift,
      height * 0.08,
      "furnitureFabric",
      true,
    ),
  );

  for (let index = 0; index < segmentCount; index += 1) {
    const t = (index / (segmentCount - 1)) * 2 - 1;
    const curve = 1 - t * t;
    const rotationZ = Math.atan((-2 * depth * 0.2 * t) / (width * 0.82));
    const x = t * width * 0.41;

    parts.push(
      box(
        `curved-seat-${index}`,
        [x, -depth * 0.18 + curve * depth * 0.1, seatZ],
        [width * 0.17, depth * 0.48, seatPartHeight],
        "furnitureFabric",
        true,
        rotationZ,
      ),
      box(
        `curved-front-apron-${index}`,
        [
          x,
          -depth * 0.4 + curve * depth * 0.12,
          baseElevation + height * 0.22 + seatLift / 2,
        ],
        [width * 0.15, depth * 0.08, height * 0.18 + seatLift],
        "furnitureFabric",
        true,
        rotationZ,
      ),
    );
  }

  const endY = -depth * 0.2;
  parts.push(
    cylinder(
      "left-rounded-end",
      [-width * 0.45, endY, baseElevation + height * 0.31 + seatLift / 2],
      height * 0.34 + seatLift,
      width * 0.08,
      depth * 0.3,
      24,
      "furnitureFabric",
      true,
    ),
    cylinder(
      "right-rounded-end",
      [width * 0.45, endY, baseElevation + height * 0.31 + seatLift / 2],
      height * 0.34 + seatLift,
      width * 0.08,
      depth * 0.3,
      24,
      "furnitureFabric",
      true,
    ),
    cylinder(
      "left-back-end",
      [-width * 0.45, depth * 0.15, backZ],
      height * 0.5 + seatLift,
      width * 0.07,
      depth * 0.18,
      20,
      "furnitureFabric",
      true,
    ),
    cylinder(
      "right-back-end",
      [width * 0.45, depth * 0.15, backZ],
      height * 0.5 + seatLift,
      width * 0.07,
      depth * 0.18,
      20,
      "furnitureFabric",
      true,
    ),
    box(
      "leg-front-left",
      [-width * 0.32, -depth * 0.38, legZ],
      [LEG_SIZE, LEG_SIZE, legHeight],
      "furnitureWood",
    ),
    box(
      "leg-front-right",
      [width * 0.32, -depth * 0.38, legZ],
      [LEG_SIZE, LEG_SIZE, legHeight],
      "furnitureWood",
    ),
    box(
      "leg-mid-left",
      [-width * 0.22, -depth * 0.08, legZ],
      [LEG_SIZE, LEG_SIZE, legHeight],
      "furnitureWood",
    ),
    box(
      "leg-mid-right",
      [width * 0.22, -depth * 0.08, legZ],
      [LEG_SIZE, LEG_SIZE, legHeight],
      "furnitureWood",
    ),
  );

  return parts;
}

function createCurvedBandPoints(
  baseY: number,
  curveY: number,
  thickness: number,
  halfWidth: number,
): [number, number][] {
  const segments = 32;
  const outer: [number, number][] = [];
  const inner: [number, number][] = [];

  for (let index = 0; index <= segments; index += 1) {
    const t = (index / segments) * 2 - 1;
    const curve = 1 - t * t;
    const x = t * halfWidth;
    const y = baseY + curve * curveY;
    outer.push([x, y + thickness / 2]);
    inner.unshift([x, y - thickness / 2]);
  }

  return [...outer, ...inner];
}

function createRoundedRectOutline(
  centerX: number,
  centerY: number,
  width: number,
  depth: number,
  radius: number,
  segments = 6,
): [number, number][] {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const cornerRadius = Math.min(radius, halfWidth * 0.48, halfDepth * 0.48);
  const centers = [
    [centerX + halfWidth - cornerRadius, centerY + halfDepth - cornerRadius],
    [centerX - halfWidth + cornerRadius, centerY + halfDepth - cornerRadius],
    [centerX - halfWidth + cornerRadius, centerY - halfDepth + cornerRadius],
    [centerX + halfWidth - cornerRadius, centerY - halfDepth + cornerRadius],
  ] as const;
  const angleRanges = [
    [0, Math.PI / 2],
    [Math.PI / 2, Math.PI],
    [Math.PI, (Math.PI * 3) / 2],
    [(Math.PI * 3) / 2, Math.PI * 2],
  ] as const;
  const points: [number, number][] = [];

  centers.forEach(([x, y], index) => {
    const [start, end] = angleRanges[index];
    for (let step = 0; step <= segments; step += 1) {
      const angle = start + ((end - start) * step) / segments;
      points.push([
        x + Math.cos(angle) * cornerRadius,
        y + Math.sin(angle) * cornerRadius,
      ]);
    }
  });

  return points;
}

function createArmchairParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const seatLift = seatSurfaceHeight - height * 0.4;
  const backShellHeight = height * 0.82 + seatLift;
  const armHeight = height * 0.48 + seatLift;
  const frontApronHeight = height * 0.2;
  // Keep the upholstered arms substantial without consuming the usable seat.
  // At the stock 600 mm footprint this leaves about 440 mm clear between them.
  const armWidth = clamp(width * 0.115, 0.055, 0.075);
  const armDepth = depth * 0.92;
  const backDepth = Math.min(Math.max(depth * 0.18, MIN_SIZE), depth * 0.26);
  const seatWidth = Math.max(width - armWidth * 2.1, MIN_SIZE);
  const seatDepth = depth * 0.72;
  const seatHeight = height * 0.17;
  const seatZ = baseElevation + seatSurfaceHeight - seatHeight;
  const legHeight = height * 0.18;
  const armTopZ = baseElevation + height * 0.49 + seatLift;
  const frontRollDepth = Math.max(depth * 0.06, MIN_SIZE);
  const frontRollRadius = Math.max(frontRollDepth * 0.5, MIN_SIZE);
  const backCenterY =
    depth / 2 - getExtrudedShapeBevelPadding(backShellHeight) - backDepth / 2;
  const frontCenterY =
    -depth / 2 +
    getExtrudedShapeBevelPadding(frontApronHeight) +
    frontRollDepth / 2;
  const leftArmCenterX =
    -width / 2 + getExtrudedShapeBevelPadding(armHeight) + armWidth / 2;
  const rightArmCenterX =
    width / 2 - getExtrudedShapeBevelPadding(armHeight) - armWidth / 2;
  const baseZ = baseElevation + height * 0.09;
  const baseHeight = Math.max(seatZ - baseZ + height * 0.01, height * 0.16);
  const frontApronZ = baseElevation + height * 0.12;
  const adjustedFrontApronHeight = Math.max(
    seatZ - frontApronZ + height * 0.01,
    frontApronHeight,
  );

  const parts: LivingRoom3DPart[] = [
    roundedSofaPart(
      "armchair-base",
      0,
      -depth * 0.08,
      baseZ,
      width * 0.84,
      depth * 0.64,
      baseHeight,
      Math.min(width, depth) * 0.07,
    ),
    roundedSofaPart(
      "armchair-back-shell",
      0,
      backCenterY,
      baseElevation + height * 0.12,
      width * 0.86,
      backDepth,
      backShellHeight,
      backDepth * 0.46,
    ),
    roundedSofaPart(
      "armchair-back-cushion",
      0,
      backCenterY - backDepth * 0.24,
      baseElevation + height * 0.27 + seatLift,
      width * 0.68,
      backDepth * 0.56,
      height * 0.56,
      backDepth * 0.26,
    ),
    roundedSofaPart(
      "armchair-left-back-wing",
      -width * 0.36,
      backCenterY - backDepth * 0.06,
      baseElevation + height * 0.16,
      armWidth * 0.88,
      backDepth * 0.9,
      height * 0.76 + seatLift,
      armWidth * 0.36,
    ),
    roundedSofaPart(
      "armchair-right-back-wing",
      width * 0.36,
      backCenterY - backDepth * 0.06,
      baseElevation + height * 0.16,
      armWidth * 0.88,
      backDepth * 0.9,
      height * 0.76 + seatLift,
      armWidth * 0.36,
    ),
    roundedSofaPart(
      "armchair-left-arm",
      leftArmCenterX,
      -depth * 0.02,
      baseElevation + height * 0.14,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.46,
    ),
    roundedSofaPart(
      "armchair-right-arm",
      rightArmCenterX,
      -depth * 0.02,
      baseElevation + height * 0.14,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.46,
    ),
    roundedSofaPart(
      "armchair-seat-cushion",
      0,
      -depth * 0.03,
      seatZ,
      seatWidth,
      seatDepth,
      seatHeight,
      Math.min(seatWidth, seatDepth) * 0.14,
    ),
    roundedSofaPart(
      "armchair-front-apron",
      0,
      frontCenterY,
      frontApronZ,
      width * 0.78,
      frontRollDepth,
      adjustedFrontApronHeight,
      frontRollDepth * 0.5,
    ),
    ...createSplayedSofaLegs(
      width * 0.64,
      depth * 0.54,
      baseElevation,
      legHeight,
      0,
      -depth * 0.08,
    ),
  ];

  parts.push(
    {
      ...cylinder(
        "armchair-back-top-roll",
        [
          0,
          depth / 2 - backDepth * 0.42,
          baseElevation + height * 0.89 + seatLift,
        ],
        width * 0.68,
        backDepth * 0.34,
        height * 0.055,
        24,
        "furnitureFabric",
        false,
        Math.PI / 2,
      ),
      rotationX: -Math.PI / 2,
    } as Extract<LivingRoom3DPart, { type: "cylinder" }>,
    {
      ...cylinder(
        "armchair-seat-front-roll",
        [
          0,
          -depth / 2 + frontRollRadius,
          baseElevation + height * 0.32 + seatLift,
        ],
        seatWidth * 0.94,
        frontRollRadius,
        height * 0.055,
        24,
        "furnitureFabric",
        false,
        Math.PI / 2,
      ),
      rotationX: -Math.PI / 2,
    } as Extract<LivingRoom3DPart, { type: "cylinder" }>,
    {
      ...cylinder(
        "armchair-left-arm-roll",
        [leftArmCenterX, -depth * 0.03, armTopZ],
        armDepth * 0.9,
        armWidth * 0.46,
        height * 0.055,
        20,
        "furnitureFabric",
      ),
      rotationX: -Math.PI / 2,
    } as Extract<LivingRoom3DPart, { type: "cylinder" }>,
    {
      ...cylinder(
        "armchair-right-arm-roll",
        [rightArmCenterX, -depth * 0.03, armTopZ],
        armDepth * 0.9,
        armWidth * 0.46,
        height * 0.055,
        20,
        "furnitureFabric",
      ),
      rotationX: -Math.PI / 2,
    } as Extract<LivingRoom3DPart, { type: "cylinder" }>,
  );

  return parts;
}

function getExtrudedShapeBevelPadding(height: number): number {
  return Math.min(Math.max(height, MIN_SIZE) * 0.03, 0.012);
}

const LOUNGE_CHAIR_1_AUTHORED_SEAT_TOP_RATIO = 0.48;
const LOUNGE_CHAIR_1_CUSHION_RIM_RATIO = 0.6;
const LOUNGE_CHAIR_1_ARM_CLEARANCE_RATIO = 0.065;
const LOUNGE_CHAIR_1_SHELL_THICKNESS_RATIO = 0.12;
const LOUNGE_CHAIR_1_CUSHION_FRONT_OVERHANG_RATIO = 0.065;
const LOUNGE_CHAIR_1_BASE_RADIUS_RATIO = 0.47;
const LOUNGE_CHAIR_1_BASE_HEIGHT = 0.045;

/** Maps traced 2D SVG coordinates onto the symbol footprint (meters). */
function mapLoungeChair1SvgToFootprint(
  points: [number, number][],
  width: number,
  depth: number,
): [number, number][] {
  const { minX, maxX, minY, maxY } = LOUNGE_CHAIR_1_SVG_BOUNDS;
  const svgWidth = Math.max(maxX - minX, MIN_SIZE);
  const svgDepth = Math.max(maxY - minY, MIN_SIZE);

  return points.map(([svgX, svgY]) => [
    ((svgX - minX) / svgWidth - 0.5) * width,
    ((maxY - svgY) / svgDepth - 0.5) * depth,
  ]);
}

function buildLoungeChair1Outlines(
  width: number,
  depth: number,
): {
  shell: [number, number][];
  cushion: [number, number][];
} {
  return {
    shell: mapLoungeChair1SvgToFootprint(
      LOUNGE_CHAIR_1_SHELL_SVG_OUTLINE,
      width,
      depth,
    ),
    cushion: mapLoungeChair1SvgToFootprint(
      LOUNGE_CHAIR_1_CUSHION_SVG_OUTLINE,
      width,
      depth,
    ),
  };
}

function createLoungeChairParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const authoredHeight = getScaledPrimitiveSymbol3DAuthoredHeight(
    modelKey,
    height,
  );
  const seatSurfaceHeight =
    getScaledPrimitiveSymbol3DReferenceHeight(modelKey, height) ??
    authoredHeight * 0.48;

  return modelKey === "living:lounge-chair-2"
    ? createLoungeChair2Parts(
        width,
        depth,
        authoredHeight,
        baseElevation,
        seatSurfaceHeight,
      )
    : createWrapLoungeChairParts(
        width,
        depth,
        authoredHeight,
        baseElevation,
        seatSurfaceHeight,
      );
}

const LOUNGE_CHAIR_2_CURVE_SEGMENTS = 48;
const LOUNGE_CHAIR_2_BODY_BOTTOM = 0.135;
const LOUNGE_CHAIR_2_BODY_TOP = 0.35;
const LOUNGE_CHAIR_2_CUSHION_HEIGHT = 0.135;
const LOUNGE_CHAIR_2_AUTHORED_SEAT_TOP_RATIO = 0.48;
const LOUNGE_CHAIR_2_LEG_HEIGHT = 0.16;

function createLoungeChair2BodyOutline(
  width: number,
  depth: number,
  segments = 56,
): [number, number][] {
  // The body receives a 26 mm outward bevel on either side, so its authored
  // outline is inset to keep the rendered shell inside the 2D symbol bounds.
  const halfWidth = width * 0.463;
  const halfDepth = depth * 0.445;
  const centerY = -depth * 0.035;
  const exponent = 3.4;

  return Array.from({ length: segments }, (_, index) => {
    const angle = (index / segments) * Math.PI * 2;
    const cosine = Math.cos(angle);
    const sine = Math.sin(angle);
    return [
      Math.sign(cosine) * Math.pow(Math.abs(cosine), 2 / exponent) * halfWidth,
      centerY +
        Math.sign(sine) * Math.pow(Math.abs(sine), 2 / exponent) * halfDepth,
    ];
  });
}

function createLoungeChair2Parts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const seatLift =
    seatSurfaceHeight - height * LOUNGE_CHAIR_2_AUTHORED_SEAT_TOP_RATIO;
  const backTop = height + seatLift;
  const wrapperHeight = Math.max(backTop - LOUNGE_CHAIR_2_BODY_BOTTOM, 0.3);
  const bodyHeight = LOUNGE_CHAIR_2_BODY_TOP - LOUNGE_CHAIR_2_BODY_BOTTOM;
  const bodyOutline = createLoungeChair2BodyOutline(width, depth);
  const cushionCenterY = -depth * 0.12;
  const legX = width * 0.355;
  const legY = depth * 0.335;
  const rearLegX = width * 0.3;
  const rearLegY = depth * 0.27;
  const createLeg = (
    id: string,
    x: number,
    y: number,
    rotationX: number,
    rotationY: number,
  ): Extract<LivingRoom3DPart, { type: "cylinder" }> => ({
    ...cylinder(
      id,
      [x, y, baseElevation + LOUNGE_CHAIR_2_LEG_HEIGHT / 2],
      LOUNGE_CHAIR_2_LEG_HEIGHT,
      width * 0.021,
      width * 0.021,
      20,
      "furnitureMetal",
      false,
    ),
    radiusTopScale: 1,
    radiusBottomScale: 0.62,
    rotationX,
    rotationY,
  });

  return [
    createLeg("lounge2-leg-front-left", -legX, -legY, 0.085, -0.07),
    createLeg("lounge2-leg-front-right", legX, -legY, 0.085, 0.07),
    createLeg("lounge2-leg-rear-left", -rearLegX, rearLegY, -0.085, -0.07),
    createLeg("lounge2-leg-rear-right", rearLegX, rearLegY, -0.085, 0.07),
    {
      ...extrudedShape(
        "lounge2-body",
        bodyOutline,
        baseElevation + LOUNGE_CHAIR_2_BODY_BOTTOM,
        bodyHeight,
        "furnitureFabric",
        false,
      ),
      bevelThickness: 0.026,
      bevelSize: 0.026,
      bevelSegments: 6,
      curveSegments: LOUNGE_CHAIR_2_CURVE_SEGMENTS,
    },
    {
      id: "lounge2-wrapper",
      type: "loungeChairShell",
      variant: "loungeChair2",
      center: [0, 0, baseElevation + LOUNGE_CHAIR_2_BODY_BOTTOM],
      width,
      depth,
      height: wrapperHeight,
      armHeight: bodyHeight,
      shellThickness: Math.min(width, depth) * 0.12,
      backSegments: 72,
      railSegments: 10,
      materialSlot: "furnitureFabric",
      useFillColor: false,
    },
    pillow(
      "lounge2-seat",
      [
        0,
        cushionCenterY,
        baseElevation + seatSurfaceHeight - LOUNGE_CHAIR_2_CUSHION_HEIGHT / 2,
      ],
      [width * 0.88, depth * 0.66, LOUNGE_CHAIR_2_CUSHION_HEIGHT],
      "furnitureFabric",
      {
        segments: [28, 28, 5],
        shape: {
          outlineExponent: 5.2,
          cornerRounding: 0.16,
          cornerPower: 3,
          rimRatio: 0.68,
          puffExponent: 0.82,
          wrinkleDepthRatio: 0,
          smoothSeams: true,
        },
      },
    ),
  ];
}

function createWrapLoungeChairParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  seatSurfaceHeight: number,
): LivingRoom3DPart[] {
  const seatLift =
    seatSurfaceHeight - height * LOUNGE_CHAIR_1_AUTHORED_SEAT_TOP_RATIO;
  const shellHeight = height + seatLift;
  const cushionHeight = Math.max(
    seatSurfaceHeight - LOUNGE_CHAIR_1_BASE_HEIGHT,
    0.12,
  );
  const cushionCenterZ = baseElevation + seatSurfaceHeight - cushionHeight / 2;
  const { cushion } = buildLoungeChair1Outlines(width, depth);
  const cushionMinX = Math.min(...cushion.map(([x]) => x));
  const cushionMaxX = Math.max(...cushion.map(([x]) => x));
  const cushionMinY = Math.min(...cushion.map(([, y]) => y));
  const cushionMaxY = Math.max(...cushion.map(([, y]) => y));
  const shellThickness = Math.min(
    Math.max(width * LOUNGE_CHAIR_1_SHELL_THICKNESS_RATIO, 0.078),
    0.105,
  );
  const cushionWidth = Math.min(
    (cushionMaxX - cushionMinX) * 1.2,
    width - shellThickness * 1.55,
  );
  const cushionDepth = (cushionMaxY - cushionMinY) * 1.25;
  const cushionCenterX = (cushionMinX + cushionMaxX) / 2;
  const cushionFrontY =
    -depth / 2 -
    Math.min(depth * LOUNGE_CHAIR_1_CUSHION_FRONT_OVERHANG_RATIO, 0.05);
  const cushionCenterY = cushionFrontY + cushionDepth / 2;
  const baseRadius = Math.min(width, depth) * LOUNGE_CHAIR_1_BASE_RADIUS_RATIO;

  return [
    cylinder(
      "lounge1-round-base",
      [0, -depth * 0.015, baseElevation + LOUNGE_CHAIR_1_BASE_HEIGHT / 2],
      LOUNGE_CHAIR_1_BASE_HEIGHT,
      baseRadius,
      baseRadius,
      64,
      "furnitureMetal",
      false,
    ),
    {
      id: "lounge1-shell",
      type: "loungeChairShell",
      center: [0, 0, baseElevation],
      width,
      depth,
      height: shellHeight,
      armHeight:
        seatSurfaceHeight + height * LOUNGE_CHAIR_1_ARM_CLEARANCE_RATIO,
      shellThickness,
      straightSegments: 16,
      backSegments: 48,
      railSegments: 12,
      materialSlot: "furnitureFabric",
      useFillColor: false,
    },
    pillow(
      "lounge1-seat-cushion",
      [cushionCenterX, cushionCenterY, cushionCenterZ],
      [cushionWidth, cushionDepth, cushionHeight],
      "furnitureFabric",
      {
        segments: [28, 28, 5],
        shape: {
          outlineExponent: 6.5,
          cornerRounding: 0.16,
          cornerPower: 3,
          rimRatio: LOUNGE_CHAIR_1_CUSHION_RIM_RATIO,
          puffExponent: 0.72,
          wrinkleDepthRatio: 0,
          smoothSeams: true,
        },
      },
    ),
  ];
}

function createChairParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const loungeScale = modelKey === "living:lounge-chair-1" ? 1 : 0.92;
  return [
    box(
      "seat",
      [0, -depth * 0.08, baseElevation + height * 0.22],
      [width * 0.74 * loungeScale, depth * 0.62, height * 0.22],
      "furnitureFabric",
      true,
    ),
    box(
      "back",
      [0, depth * 0.35, baseElevation + height * 0.54],
      [width * 0.78, Math.max(depth * 0.14, MIN_SIZE), height * 0.86],
      "furnitureFabric",
      true,
    ),
    box(
      "left-arm",
      [-width * 0.43, -depth * 0.02, baseElevation + height * 0.38],
      [width * 0.12, depth * 0.68, height * 0.48],
      "furnitureFabric",
      true,
    ),
    box(
      "right-arm",
      [width * 0.43, -depth * 0.02, baseElevation + height * 0.38],
      [width * 0.12, depth * 0.68, height * 0.48],
      "furnitureFabric",
      true,
    ),
    ...createLegs(width * 0.58, depth * 0.48, baseElevation, height * 0.16),
  ];
}

function createCoffeeTableParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const topHeight = Math.min(height * 0.16, 0.08);
  const topZ = baseElevation + height;

  if (modelKey === "living:coffee-table-organic") {
    return [
      extrudedShape(
        "tabletop",
        createOrganicOutline(width, depth, 0.96, true),
        topZ - topHeight,
        topHeight,
        "furnitureWood",
        true,
      ),
      cylinder(
        "pedestal",
        [0, 0, baseElevation + height * 0.45],
        height * 0.72,
        width * 0.08,
        depth * 0.12,
        24,
        "furnitureWood",
        true,
      ),
      extrudedShape(
        "base",
        createOrganicOutline(width, depth, 0.46, true),
        baseElevation + height * 0.04,
        height * 0.08,
        "furnitureWood",
        true,
      ),
    ];
  }

  const segments =
    modelKey === "living:coffee-table-pentagonal"
      ? 5
      : modelKey === "living:coffee-table-hexagonal"
        ? 6
        : modelKey === "living:coffee-table-octagonal"
          ? 8
          : 32;
  const parts: LivingRoom3DPart[] = [
    modelKey === "living:coffee-table-oval"
      ? extrudedShape(
          "tabletop",
          createCapsuleCoffeeTableOutline(width, depth, 0.96),
          topZ - topHeight,
          topHeight,
          "furnitureWood",
          true,
        )
      : cylinder(
          "tabletop",
          [0, 0, topZ - topHeight / 2],
          topHeight,
          width * 0.48,
          depth * 0.48,
          segments,
          "furnitureWood",
          true,
        ),
  ];

  if (modelKey === "living:coffee-table-oval") {
    parts.push(
      cylinder(
        "pedestal",
        [0, 0, baseElevation + height * 0.45],
        height * 0.72,
        width * 0.08,
        depth * 0.12,
        24,
        "furnitureWood",
        true,
      ),
      extrudedShape(
        "base",
        createCapsuleCoffeeTableOutline(width, depth, 0.46),
        baseElevation + height * 0.04,
        height * 0.08,
        "furnitureWood",
        true,
      ),
    );
  } else {
    parts.push(
      ...createLegs(
        width * 0.44,
        depth * 0.44,
        baseElevation,
        height - topHeight,
        0,
        0,
        "furnitureMetal",
      ),
    );
  }

  return parts;
}

function createCapsuleCoffeeTableOutline(
  width: number,
  depth: number,
  scale: number,
): [number, number][] {
  return createCapsuleOutline(width, depth, scale);
}

function createCapsuleOutline(
  width: number,
  depth: number,
  scale = 1,
  centerX = 0,
  centerY = 0,
  arcSegments = 18,
): [number, number][] {
  const halfWidth = (width * scale) / 2;
  const halfDepth = (depth * scale) / 2;
  const radius = Math.min(halfDepth, halfWidth);
  const straightHalfWidth = Math.max(halfWidth - radius, 0);
  const points: [number, number][] = [
    [centerX - straightHalfWidth, centerY + halfDepth],
    [centerX + straightHalfWidth, centerY + halfDepth],
  ];

  for (let i = 1; i <= arcSegments; i += 1) {
    const angle = Math.PI / 2 - (i * Math.PI) / arcSegments;
    points.push([
      centerX + straightHalfWidth + Math.cos(angle) * radius,
      centerY + Math.sin(angle) * radius,
    ]);
  }

  points.push([centerX - straightHalfWidth, centerY - halfDepth]);

  for (let i = 1; i <= arcSegments; i += 1) {
    const angle = -Math.PI / 2 - (i * Math.PI) / arcSegments;
    points.push([
      centerX - straightHalfWidth + Math.cos(angle) * radius,
      centerY + Math.sin(angle) * radius,
    ]);
  }

  return points;
}

function createEllipseOutline(
  centerX: number,
  centerY: number,
  width: number,
  depth: number,
  segments = 36,
): [number, number][] {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  return Array.from({ length: segments }, (_, index) => {
    const angle = (index / segments) * Math.PI * 2;
    return [
      centerX + Math.cos(angle) * halfWidth,
      centerY + Math.sin(angle) * halfDepth,
    ];
  });
}

function createChamferedRectOutline(
  width: number,
  depth: number,
  chamfer: number,
): [number, number][] {
  const halfWidth = width / 2;
  const halfDepth = depth / 2;
  const corner = Math.min(chamfer, halfWidth * 0.3, halfDepth * 0.3);

  return [
    [-halfWidth + corner, -halfDepth],
    [halfWidth - corner, -halfDepth],
    [halfWidth, -halfDepth + corner],
    [halfWidth, halfDepth - corner],
    [halfWidth - corner, halfDepth],
    [-halfWidth + corner, halfDepth],
    [-halfWidth, halfDepth - corner],
    [-halfWidth, -halfDepth + corner],
  ];
}

function createOrganicOutline(
  width: number,
  depth: number,
  scale: number,
  convertFromLegacySvgCoordinates = false,
): [number, number][] {
  // The coffee-table contour was sampled from its legacy y-down SVG. Built-in
  // 3D geometry is authored in the current y-up convention because the render
  // wrapper composes the legacy/user flip afterward. Convert only that table;
  // the organic bush already relies on the historical procedural orientation.
  const yDirection = convertFromLegacySvgCoordinates ? -1 : 1;
  return ORGANIC_COFFEE_TABLE_OUTLINE.map(([x, y]) => [
    x * width * scale,
    y * depth * scale * yDirection,
  ]);
}

function createScreenParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const panelThickness = Math.max(Math.min(depth * 0.36, 0.08), 0.035);
  return [
    box(
      "panel",
      [0, 0, baseElevation + height * 0.58],
      [width * 0.82, panelThickness, height * 0.76],
      "furnitureScreen",
    ),
    box(
      "frame",
      [0, 0, baseElevation + height * 0.58],
      [width * 0.9, panelThickness * 1.25, height * 0.84],
      "furnitureMetal",
    ),
    box(
      "stand",
      [0, 0, baseElevation + height * 0.18],
      [width * 0.08, panelThickness, height * 0.28],
      "furnitureMetal",
    ),
    box(
      "base",
      [0, 0, baseElevation + height * 0.04],
      [width * 0.42, depth, height * 0.08],
      "furnitureMetal",
    ),
  ];
}

function createLoungeBaseParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  offsetX: number,
  offsetY: number,
): LivingRoom3DPart[] {
  return [
    box(
      "seat",
      [offsetX, offsetY - depth * 0.04, baseElevation + height * 0.22],
      [width * 0.82, depth * 0.68, height * 0.28],
      "furnitureFabric",
      true,
    ),
    ...createLegs(
      width * 0.72,
      depth * 0.52,
      baseElevation,
      height * 0.16,
      offsetX,
      offsetY - depth * 0.04,
    ),
  ];
}

function createLegs(
  width: number,
  depth: number,
  baseElevation: number,
  legHeight: number,
  offsetX = 0,
  offsetY = 0,
  materialSlot: FloorPlan3DMaterialSlot = "furnitureWood",
): LivingRoom3DPart[] {
  const x = width / 2;
  const y = depth / 2;
  const z = baseElevation + legHeight / 2;
  return [
    box(
      "leg-nw",
      [offsetX - x, offsetY + y, z],
      [LEG_SIZE, LEG_SIZE, legHeight],
      materialSlot,
    ),
    box(
      "leg-ne",
      [offsetX + x, offsetY + y, z],
      [LEG_SIZE, LEG_SIZE, legHeight],
      materialSlot,
    ),
    box(
      "leg-sw",
      [offsetX - x, offsetY - y, z],
      [LEG_SIZE, LEG_SIZE, legHeight],
      materialSlot,
    ),
    box(
      "leg-se",
      [offsetX + x, offsetY - y, z],
      [LEG_SIZE, LEG_SIZE, legHeight],
      materialSlot,
    ),
  ];
}

function createBotanicalParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  if (modelKey === "botanical:tree-branch") {
    return createBranchTreeParts(width, depth, height, baseElevation);
  }

  if (modelKey === "botanical:tree-canopy") {
    return createCanopyTreeParts(width, depth, height, baseElevation);
  }

  if (modelKey === "botanical:plant") {
    return createPottedPlantParts(width, depth, height, baseElevation);
  }

  return createBushParts(modelKey, width, depth, height, baseElevation);
}

function getDirectionCoords(
  angle: number,
  radiusX: number,
  radiusY: number,
): [number, number] {
  return [Math.cos(angle) * radiusX, Math.sin(angle) * radiusY];
}

function createCanopyTreeParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const trunkBaseHeight = height * 0.25;
  const trunkMidHeight = height * 0.25;
  const trunkTopHeight = height * 0.14;

  return [
    cylinder(
      "trunk",
      [0, 0, baseElevation + trunkBaseHeight / 2],
      trunkBaseHeight,
      width * 0.06,
      depth * 0.066,
      10,
      "botanicalBranch",
    ),
    cylinder(
      "trunk-mid",
      [0, 0, baseElevation + trunkBaseHeight + trunkMidHeight / 2],
      trunkMidHeight,
      width * 0.045,
      depth * 0.05,
      10,
      "botanicalBranch",
    ),
    cylinder(
      "trunk-top",
      [
        0,
        0,
        baseElevation + trunkBaseHeight + trunkMidHeight + trunkTopHeight / 2,
      ],
      trunkTopHeight,
      width * 0.035,
      depth * 0.04,
      10,
      "botanicalBranch",
    ),
    ...createCanopyCrownParts(width, depth, height, baseElevation),
    ...createCanopyLeafParts(width, depth, height, baseElevation),
    ...createCanopyBranchParts(width, depth, height, baseElevation),
  ];
}

/** Golden-angle spacing for evenly distributed foliage clusters. */
const CANOPY_FOLIAGE_GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/** Deterministic micro-offset so dense foliage does not line up on a visible grid. */
function getCanopyFoliageJitter(
  seed: number,
  width: number,
  depth: number,
  height: number,
): [number, number, number] {
  return [
    Math.sin(seed * 12.9898) * width * 0.0055,
    Math.cos(seed * 78.233) * depth * 0.0055,
    Math.sin(seed * 43.758) * height * 0.007,
  ];
}

/** Shared vertical bounds so crown and perimeter foliage align with branch tips. */
function getCanopyFoliageVerticalLayout(
  height: number,
  baseElevation: number,
): { crownZBase: number; crownHeight: number } {
  return {
    crownZBase: baseElevation + height * 0.35,
    crownHeight: height * 0.45,
  };
}

/**
 * Builds a dome of many tiny circular foliage discs so the canopy reads as
 * leaf texture instead of large stacked blocks.
 */
function createCanopyCrownParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const { crownZBase, crownHeight } = getCanopyFoliageVerticalLayout(
    height,
    baseElevation,
  );
  const canopyRadiusX = width * 0.34;
  const canopyRadiusY = depth * 0.34;
  const parts: LivingRoom3DPart[] = [];

  const layers = [
    { count: 400, radialScale: 1, zBias: 0, angleOffset: 0 },
    {
      count: 280,
      radialScale: 0.92,
      zBias: 0.1,
      angleOffset: CANOPY_FOLIAGE_GOLDEN_ANGLE * 0.5,
    },
    {
      count: 200,
      radialScale: 0.8,
      zBias: -0.08,
      angleOffset: CANOPY_FOLIAGE_GOLDEN_ANGLE * 0.25,
    },
    {
      count: 140,
      radialScale: 0.62,
      zBias: 0.04,
      angleOffset: CANOPY_FOLIAGE_GOLDEN_ANGLE * 0.75,
    },
  ];

  let globalIndex = 0;

  layers.forEach(({ count, radialScale, zBias, angleOffset }) => {
    for (let index = 0; index < count; index += 1) {
      const radialT = Math.sqrt((index + 0.5) / count) * radialScale;
      const angle = index * CANOPY_FOLIAGE_GOLDEN_ANGLE + angleOffset;
      const [jitterX, jitterY, jitterZ] = getCanopyFoliageJitter(
        globalIndex + 1,
        width,
        depth,
        height,
      );
      const x = Math.cos(angle) * radialT * canopyRadiusX + jitterX;
      const y = Math.sin(angle) * radialT * canopyRadiusY + jitterY;

      const distNorm = Math.min(radialT / 0.95, 1);
      const domeFactor = Math.sqrt(Math.max(0, 1 - distNorm * distNorm * 0.85));
      const sizeJitter =
        (globalIndex % 7) * 0.0007 + (globalIndex % 3) * 0.0004;
      const clusterRadius = width * (0.0055 + domeFactor * 0.0045 + sizeJitter);
      const clusterHeight =
        height * (0.0032 + domeFactor * 0.0028 + (globalIndex % 4) * 0.0005);
      const z =
        crownZBase +
        crownHeight * (0.06 + 0.72 * domeFactor + zBias) +
        clusterHeight / 2 +
        jitterZ;

      parts.push(
        foliageCylinder(
          globalIndex === 0 ? "canopy-center" : `canopy-cluster-${globalIndex}`,
          [x, y, z],
          clusterHeight,
          clusterRadius,
          clusterRadius,
          6,
          "botanicalFoliage",
          false,
        ),
      );

      globalIndex += 1;
    }
  });

  return parts;
}

function createCanopyBranchParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const trunkColumnHeight = height * 0.58;
  const mainRadiusX = width * 0.026;
  const mainRadiusY = depth * 0.026;
  const stemRadiusX = width * 0.016;
  const stemRadiusY = depth * 0.016;
  const twigRadiusX = width * 0.01;
  const twigRadiusY = depth * 0.01;
  const branchWidth = width * 0.024;
  const branchHeight = height * 0.024;

  const parts: LivingRoom3DPart[] = [
    cylinder(
      "trunk-column",
      [0, 0, baseElevation + trunkColumnHeight / 2],
      trunkColumnHeight,
      mainRadiusX,
      mainRadiusY,
      10,
      "botanicalBranch",
    ),
  ];

  const directions = [
    { name: "east", angle: 0 },
    { name: "north-east", angle: Math.PI / 4 },
    { name: "north", angle: Math.PI / 2 },
    { name: "north-west", angle: (3 * Math.PI) / 4 },
    { name: "west", angle: Math.PI },
    { name: "south-west", angle: (5 * Math.PI) / 4 },
    { name: "south", angle: (3 * Math.PI) / 2 },
    { name: "south-east", angle: (7 * Math.PI) / 4 },
  ];

  directions.forEach(({ name, angle }) => {
    const isCardinal =
      name === "east" ||
      name === "north" ||
      name === "west" ||
      name === "south";
    const branchStartHeight = baseElevation + height * (isCardinal ? 0.4 : 0.5);

    const brX = width * 0.17;
    const brY = depth * 0.17;
    const [bx, by] = getDirectionCoords(angle, brX, brY);
    const L = Math.hypot(bx, by);

    parts.push(
      box(
        `branch-horizontal-${name}`,
        [bx / 2, by / 2, branchStartHeight],
        [L, branchWidth, branchHeight],
        "botanicalBranch",
        false,
        angle,
      ),
    );

    const riserTop = baseElevation + height * 0.54;
    const brHeight = Math.max(riserTop - branchStartHeight, MIN_SIZE);
    const brZ = branchStartHeight + brHeight / 2;

    parts.push(
      cylinder(
        `branch-riser-${name}`,
        [bx, by, brZ],
        brHeight,
        stemRadiusX,
        stemRadiusY,
        8,
        "botanicalBranch",
      ),
    );

    const twX = width * 0.22;
    const twY = depth * 0.22;
    const [tx, ty] = getDirectionCoords(angle, twX, twY);
    const twHeight = height * 0.1;
    const twZ = baseElevation + height * 0.54 + twHeight / 2;

    parts.push(
      cylinder(
        `twig-upright-${name}`,
        [tx, ty, twZ],
        twHeight,
        twigRadiusX,
        twigRadiusY,
        7,
        "botanicalBranch",
      ),
    );
  });

  return parts;
}

function createCanopyLeafParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const { crownZBase, crownHeight } = getCanopyFoliageVerticalLayout(
    height,
    baseElevation,
  );
  const parts: LivingRoom3DPart[] = [];

  const directions = [
    { name: "east", angle: 0 },
    { name: "north-east", angle: Math.PI / 4 },
    { name: "north", angle: Math.PI / 2 },
    { name: "north-west", angle: (3 * Math.PI) / 4 },
    { name: "west", angle: Math.PI },
    { name: "south-west", angle: (5 * Math.PI) / 4 },
    { name: "south", angle: (3 * Math.PI) / 2 },
    { name: "south-east", angle: (7 * Math.PI) / 4 },
  ];

  directions.forEach(({ name, angle }) => {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const leafCount = 16;

    for (let leafIndex = 0; leafIndex < leafCount; leafIndex += 1) {
      const ringT = 0.86 + leafIndex * 0.012;
      const spread = (leafIndex - (leafCount - 1) / 2) * width * 0.009;
      const [jitterX, jitterY, jitterZ] = getCanopyFoliageJitter(
        leafIndex + angle * 10,
        width,
        depth,
        height,
      );
      const x = cos * width * 0.34 * ringT - sin * spread + jitterX;
      const y = sin * depth * 0.34 * ringT + cos * spread + jitterY;
      const leafRadius = width * (0.005 + (leafIndex % 3) * 0.001);
      const leafHeight = height * (0.0028 + (leafIndex % 2) * 0.0007);
      const leafZ =
        crownZBase +
        crownHeight * (0.18 + leafIndex * 0.028) +
        leafHeight / 2 +
        jitterZ;

      parts.push(
        foliageCylinder(
          leafIndex === 0
            ? `canopy-leaf-${name}`
            : `canopy-leaf-${name}-${leafIndex}`,
          [x, y, leafZ],
          leafHeight,
          leafRadius,
          leafRadius,
          6,
          "botanicalFoliage",
          false,
        ),
      );
    }
  });

  return parts;
}

function createBranchTreeParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const trunkBaseHeight = height * 0.3;
  const trunkMidHeight = height * 0.25;
  const trunkTopHeight = height * 0.2;
  const branchWidth = width * 0.024;
  const branchHeight = height * 0.024;
  const twigWidth = width * 0.015;
  const twigHeight = height * 0.015;

  const parts: LivingRoom3DPart[] = [
    cylinder(
      "trunk",
      [0, 0, baseElevation + trunkBaseHeight / 2],
      trunkBaseHeight,
      width * 0.05,
      depth * 0.055,
      10,
      "botanicalBranch",
    ),
    cylinder(
      "trunk-mid",
      [0, 0, baseElevation + trunkBaseHeight + trunkMidHeight / 2],
      trunkMidHeight,
      width * 0.04,
      depth * 0.045,
      10,
      "botanicalBranch",
    ),
    cylinder(
      "trunk-top",
      [
        0,
        0,
        baseElevation + trunkBaseHeight + trunkMidHeight + trunkTopHeight / 2,
      ],
      trunkTopHeight,
      width * 0.03,
      depth * 0.035,
      10,
      "botanicalBranch",
    ),
  ];

  const directions = [
    { name: "east", angle: 0 },
    { name: "north-east", angle: Math.PI / 4 },
    { name: "north", angle: Math.PI / 2 },
    { name: "north-west", angle: (3 * Math.PI) / 4 },
    { name: "west", angle: Math.PI },
    { name: "south-west", angle: (5 * Math.PI) / 4 },
    { name: "south", angle: (3 * Math.PI) / 2 },
    { name: "south-east", angle: (7 * Math.PI) / 4 },
  ];

  directions.forEach(({ name, angle }) => {
    const isCardinal =
      name === "east" ||
      name === "north" ||
      name === "west" ||
      name === "south";
    const branchStartHeight =
      baseElevation + height * (isCardinal ? 0.45 : 0.58);

    const brX = width * 0.22;
    const brY = depth * 0.22;
    const [bx, by] = getDirectionCoords(angle, brX, brY);
    const L = Math.hypot(bx, by);

    parts.push(
      box(
        `branch-${name}`,
        [bx / 2, by / 2, branchStartHeight],
        [L, branchWidth, branchHeight],
        "botanicalBranch",
        false,
        angle,
      ),
    );

    const riserTopHeight = baseElevation + height * 0.72;
    const brHeight = Math.max(riserTopHeight - branchStartHeight, MIN_SIZE);
    const brZ = branchStartHeight + brHeight / 2;
    parts.push(
      cylinder(
        `branch-riser-${name}`,
        [bx, by, brZ],
        brHeight,
        width * 0.015,
        depth * 0.015,
        8,
        "botanicalBranch",
      ),
    );

    const twigLength = width * 0.14;
    const angleA = angle + Math.PI / 10;
    const angleB = angle - Math.PI / 10;

    const txA = bx + Math.cos(angleA) * (twigLength / 2);
    const tyA = by + Math.sin(angleA) * (twigLength / 2);
    parts.push(
      box(
        `twig-${name}-a`,
        [txA, tyA, riserTopHeight],
        [twigLength, twigWidth, twigHeight],
        "botanicalBranch",
        false,
        angleA,
      ),
    );

    const txB = bx + Math.cos(angleB) * (twigLength / 2);
    const tyB = by + Math.sin(angleB) * (twigLength / 2);
    parts.push(
      box(
        `twig-${name}-b`,
        [txB, tyB, riserTopHeight],
        [twigLength, twigWidth, twigHeight],
        "botanicalBranch",
        false,
        angleB,
      ),
    );

    const endX_A = bx + Math.cos(angleA) * twigLength;
    const endY_A = by + Math.sin(angleA) * twigLength;
    const endX_B = bx + Math.cos(angleB) * twigLength;
    const endY_B = by + Math.sin(angleB) * twigLength;
    const twigRiserHeight = height * 0.08;
    const twigRiserZ = riserTopHeight + twigRiserHeight / 2;

    parts.push(
      cylinder(
        `twig-riser-${name}-a`,
        [endX_A, endY_A, twigRiserZ],
        twigRiserHeight,
        width * 0.008,
        depth * 0.008,
        6,
        "botanicalBranch",
      ),
      cylinder(
        `twig-riser-${name}-b`,
        [endX_B, endY_B, twigRiserZ],
        twigRiserHeight,
        width * 0.008,
        depth * 0.008,
        6,
        "botanicalBranch",
      ),
    );

    const leafZ = riserTopHeight + twigRiserHeight;
    const leafHeight = height * 0.02;
    const leafLen = width * 0.06;
    const leafWid = depth * 0.024;

    parts.push(
      extrudedShape(
        `leaf-${name}`,
        createLeafOutline(endX_A, endY_A, leafLen, leafWid, angleA),
        leafZ,
        leafHeight,
        "botanicalFoliage",
        false,
      ),
      extrudedShape(
        `leaf-twig-b-${name}`,
        createLeafOutline(endX_B, endY_B, leafLen, leafWid, angleB),
        leafZ,
        leafHeight,
        "botanicalFoliage",
        false,
      ),
    );
  });

  return parts;
}

function createPottedPlantParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const parts: LivingRoom3DPart[] = [];

  // Pot main body
  parts.push(
    cylinder(
      "pot",
      [0, 0, baseElevation + height * 0.12],
      height * 0.24,
      width * 0.16,
      depth * 0.16,
      18,
      "botanicalPot",
    ),
  );

  // Pot rim
  parts.push(
    cylinder(
      "pot-rim",
      [0, 0, baseElevation + height * 0.26],
      height * 0.04,
      width * 0.175,
      depth * 0.175,
      18,
      "botanicalPot",
    ),
  );

  // Soil inside pot
  parts.push(
    cylinder(
      "pot-soil",
      [0, 0, baseElevation + height * 0.25],
      height * 0.02,
      width * 0.155,
      depth * 0.155,
      16,
      "botanicalBranch",
    ),
  );

  // Central crown core
  const coreZ = baseElevation + height * 0.34;
  parts.push(
    cylinder(
      "crown-core",
      [0, 0, coreZ + height * 0.1],
      height * 0.2,
      width * 0.12,
      depth * 0.12,
      12,
      "botanicalFoliage",
      false,
    ),
  );

  // Tier 1: Outer Rosette Leaves (8 leaves)
  const tier1Z = baseElevation + height * 0.28;
  const tier1Height = height * 0.06;
  const t1Len = width * 0.45;
  const t1Wid = depth * 0.13;

  for (let index = 0; index < 8; index += 1) {
    const angle = (index / 8) * Math.PI * 2;
    const dist = t1Len * 0.25;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist;

    parts.push(
      extrudedShape(
        `leaf-${index}`,
        createLeafOutline(cx, cy, t1Len, t1Wid, angle),
        tier1Z + index * 0.001,
        tier1Height,
        "botanicalFoliage",
        false,
      ),
    );
  }

  // Tier 2: Middle Rosette Leaves (8 leaves, rotated by pi/8)
  const tier2Z = baseElevation + height * 0.35;
  const tier2Height = height * 0.05;
  const t2Len = width * 0.36;
  const t2Wid = depth * 0.11;

  for (let index = 0; index < 8; index += 1) {
    const angle = (index / 8) * Math.PI * 2 + Math.PI / 8;
    const dist = t2Len * 0.25;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist;

    parts.push(
      extrudedShape(
        `leaf-mid-${index}`,
        createLeafOutline(cx, cy, t2Len, t2Wid, angle),
        tier2Z + index * 0.001,
        tier2Height,
        "botanicalFoliage",
        false,
      ),
    );
  }

  // Tier 3: Inner Rounded Rosette Leaves (5 leaves)
  const tier3Z = baseElevation + height * 0.42;
  const tier3Height = height * 0.04;
  const t3Len = width * 0.25;
  const t3Wid = depth * 0.12;

  for (let index = 0; index < 5; index += 1) {
    const angle = (index / 5) * Math.PI * 2;
    const dist = t3Len * 0.25;
    const cx = Math.cos(angle) * dist;
    const cy = Math.sin(angle) * dist;

    parts.push(
      extrudedShape(
        `leaf-inner-${index}`,
        createLeafOutline(cx, cy, t3Len, t3Wid, angle),
        tier3Z + index * 0.001,
        tier3Height,
        "botanicalFoliage",
        false,
      ),
    );
  }

  // Tier 4: Upright central spike leaves
  const spikeZ = baseElevation + height * 0.44;
  const spikeHeight = height * 0.32;
  const spikeRadiusX = width * 0.015;
  const spikeRadiusY = depth * 0.015;

  for (let index = 0; index < 4; index += 1) {
    const angle = (index / 4) * Math.PI * 2 + Math.PI / 4;
    const cx = Math.cos(angle) * width * 0.03;
    const cy = Math.sin(angle) * depth * 0.03;

    parts.push(
      cylinder(
        `leaf-spike-${index}`,
        [cx, cy, spikeZ + spikeHeight / 2],
        spikeHeight,
        spikeRadiusX,
        spikeRadiusY,
        6,
        "botanicalFoliage",
        false,
      ),
    );
  }

  return parts;
}

/** Deterministic 0–1 jitter for stable hedge foliage placement. */
function hedgeHashJitter(seed: number): number {
  const value = Math.sin(seed * 127.1 + seed * 311.7) * 43758.5453;
  return value - Math.floor(value);
}

/** Tests whether a plan point lies inside a horizontal stadium/capsule footprint. */
function isInsideCapsule(
  x: number,
  y: number,
  halfWidth: number,
  halfDepth: number,
  inset = 0,
): boolean {
  const radius = Math.min(halfDepth, halfWidth) - inset;
  const straightHalf = Math.max(halfWidth - Math.min(halfDepth, halfWidth), 0);
  if (radius <= 0) {
    return false;
  }

  const ax = Math.abs(x);
  const ay = Math.abs(y);
  if (ax <= straightHalf) {
    return ay <= radius;
  }

  const dx = ax - straightHalf;
  return dx * dx + ay * ay <= radius * radius;
}

/** Returns a point on the capsule perimeter (t in 0..1, stadium aligned on X). */
function getCapsulePerimeterPoint(
  t: number,
  halfWidth: number,
  halfDepth: number,
): [number, number] {
  const radius = Math.min(halfDepth, halfWidth);
  const straightHalf = Math.max(halfWidth - radius, 0);
  const arcLength = Math.PI * radius;
  const topLength = 2 * straightHalf;
  const total = topLength * 2 + arcLength * 2;
  let distance = (((t % 1) + 1) % 1) * total;

  if (distance <= topLength) {
    return [straightHalf - distance, radius];
  }
  distance -= topLength;

  if (distance <= arcLength) {
    const angle = Math.PI / 2 + distance / radius;
    return [-straightHalf + Math.cos(angle) * radius, Math.sin(angle) * radius];
  }
  distance -= arcLength;

  if (distance <= topLength) {
    return [-straightHalf + distance, -radius];
  }
  distance -= topLength;

  const angle = -Math.PI / 2 + distance / radius;
  return [straightHalf + Math.cos(angle) * radius, Math.sin(angle) * radius];
}

/** Spacing multiplier for bush top foliage grids (higher = fewer instances). */
const BUSH_TOP_SPACING_MULTIPLIER = 2.55;

/** Tests whether a plan point lies inside an axis-aligned ellipse. */
function isInsideEllipse(
  x: number,
  y: number,
  halfWidth: number,
  halfDepth: number,
  inset = 0,
): boolean {
  const hw = Math.max(halfWidth - inset, MIN_SIZE);
  const hd = Math.max(halfDepth - inset, MIN_SIZE);
  const nx = x / hw;
  const ny = y / hd;
  return nx * nx + ny * ny <= 1;
}

/** Returns a point on an ellipse perimeter (t in 0..1). */
function getEllipsePerimeterPoint(
  t: number,
  halfWidth: number,
  halfDepth: number,
): [number, number] {
  const angle = (((t % 1) + 1) % 1) * Math.PI * 2;
  return [Math.cos(angle) * halfWidth, Math.sin(angle) * halfDepth];
}

/** Tests whether a plan point lies inside a closed polygon. */
function isInsidePolygon(
  x: number,
  y: number,
  points: [number, number][],
): boolean {
  let inside = false;
  for (
    let index = 0, previous = points.length - 1;
    index < points.length;
    previous = index++
  ) {
    const [xi, yi] = points[index];
    const [xj, yj] = points[previous];
    const intersects =
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi + Number.EPSILON) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

/** Returns a point along a closed polygon perimeter (t in 0..1). */
function getPolygonPerimeterPoint(
  t: number,
  points: [number, number][],
): [number, number] {
  const segments: Array<{
    length: number;
    from: [number, number];
    to: [number, number];
  }> = [];
  let total = 0;
  for (let index = 0; index < points.length; index += 1) {
    const from = points[index];
    const to = points[(index + 1) % points.length];
    const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
    segments.push({ length, from, to });
    total += length;
  }

  let distance = (((t % 1) + 1) % 1) * total;
  for (const segment of segments) {
    if (distance <= segment.length) {
      const ratio = distance / (segment.length || 1);
      return [
        segment.from[0] + (segment.to[0] - segment.from[0]) * ratio,
        segment.from[1] + (segment.to[1] - segment.from[1]) * ratio,
      ];
    }
    distance -= segment.length;
  }

  return points[0];
}

type BushFoliageFootprint = {
  getPerimeterPoint: (t: number, scale: number) => [number, number];
  getOutwardNormal: (x: number, y: number) => [number, number];
  isInside: (x: number, y: number, inset: number) => boolean;
  halfWidth: number;
  halfDepth: number;
};

type BushFoliageCylinderInstance = {
  center: [number, number, number];
  height: number;
  radiusX: number;
  radiusY: number;
  rotationZ?: number;
};

function createBushFoliageInstance(
  x: number,
  y: number,
  centerZ: number,
  blobRadius: number,
  seed: number,
): BushFoliageCylinderInstance {
  const jitterX = (hedgeHashJitter(seed + 1) - 0.5) * blobRadius * 1.1;
  const jitterY = (hedgeHashJitter(seed + 2) - 0.5) * blobRadius * 1.1;
  const radius = blobRadius * (0.68 + hedgeHashJitter(seed + 3) * 0.64);
  const blobHeight = radius * 2 * (0.78 + hedgeHashJitter(seed + 5) * 0.44);

  return {
    center: [
      x + jitterX,
      y + jitterY,
      centerZ + radius * (0.85 + hedgeHashJitter(seed + 9) * 0.3),
    ],
    height: blobHeight,
    radiusX: radius,
    radiusY: radius * (0.88 + hedgeHashJitter(seed + 11) * 0.16),
  };
}

function assembleInstancedBushFoliage(
  instances: BushFoliageCylinderInstance[],
  instancedPartId: string,
  markerPartIds: Record<number, string> = {},
): LivingRoom3DPart[] {
  const instanced: BushFoliageCylinderInstance[] = [];
  const markers: LivingRoom3DPart[] = [];

  instances.forEach((instance, index) => {
    const markerId = markerPartIds[index];
    if (markerId) {
      markers.push({
        id: markerId,
        type: "cylinder",
        center: instance.center,
        height: instance.height,
        radiusX: instance.radiusX,
        radiusY: instance.radiusY,
        radialSegments: 6,
        materialSlot: "botanicalFoliage",
        useFillColor: false,
        rotationZ: instance.rotationZ,
        skipSelectionOutline: true,
      });
      return;
    }

    instanced.push(instance);
  });

  if (instanced.length === 0) {
    return markers;
  }

  return [
    ...markers,
    {
      id: instancedPartId,
      type: "instancedCylinders",
      instances: instanced,
      radialSegments: 6,
      materialSlot: "botanicalFoliage",
      useFillColor: false,
      skipSelectionOutline: true,
    },
  ];
}

function collectBushShellInstances(
  height: number,
  baseElevation: number,
  blobRadius: number,
  footprint: BushFoliageFootprint,
  options: {
    seedOffset?: number;
    shellRings?: number;
    shellLayers?: number;
    pointsPerRing?: number;
  } = {},
): BushFoliageCylinderInstance[] {
  const shellRings = options.shellRings ?? 3;
  const shellLayers = options.shellLayers ?? 4;
  const pointsPerRing = options.pointsPerRing ?? 44;
  const seedOffset = options.seedOffset ?? 0;
  const zStart = baseElevation + height * 0.08;
  const zSpan = height * 0.82;
  const instances: BushFoliageCylinderInstance[] = [];

  for (let ring = 0; ring < shellRings; ring += 1) {
    const ringScale = 1 - ring * 0.018;
    for (let layer = 0; layer < shellLayers; layer += 1) {
      for (let index = 0; index < pointsPerRing; index += 1) {
        const perimeterT = (index + layer * 0.21 + ring * 0.11) / pointsPerRing;
        const [px, py] = footprint.getPerimeterPoint(perimeterT, ringScale);
        const [normalX, normalY] = footprint.getOutwardNormal(px, py);
        const outward = blobRadius * (0.35 - ring * 0.08);
        const layerT = (layer + ring * 0.7) / shellLayers;
        const centerZ =
          zStart +
          layerT * zSpan +
          (hedgeHashJitter(seedOffset + instances.length * 13 + 1) - 0.5) *
            height *
            0.035;

        instances.push(
          createBushFoliageInstance(
            px + normalX * outward,
            py + normalY * outward,
            centerZ,
            blobRadius,
            seedOffset + instances.length * 3,
          ),
        );
      }
    }
  }

  return instances;
}

function collectBushTopInstances(
  height: number,
  baseElevation: number,
  blobRadius: number,
  footprint: BushFoliageFootprint,
  seedOffset: number,
): BushFoliageCylinderInstance[] {
  const spacingX = blobRadius * BUSH_TOP_SPACING_MULTIPLIER;
  const spacingY = blobRadius * (BUSH_TOP_SPACING_MULTIPLIER * 0.94);
  const coreHeight = height * 0.86;
  const coreZ = baseElevation + height * 0.07;
  const topZ = coreZ + coreHeight;
  const instances: BushFoliageCylinderInstance[] = [];

  let row = 0;
  for (
    let y = -footprint.halfDepth + spacingY * 0.5;
    y <= footprint.halfDepth;
    y += spacingY
  ) {
    const rowStagger = row % 2 === 0 ? 0 : spacingX * 0.5;
    for (
      let x = -footprint.halfWidth + spacingX * 0.5 + rowStagger;
      x <= footprint.halfWidth;
      x += spacingX
    ) {
      if (!footprint.isInside(x, y, blobRadius * 0.15)) {
        continue;
      }

      const seed = seedOffset + instances.length * 19;
      const radius = blobRadius * (0.72 + hedgeHashJitter(seed + 3) * 0.56);
      const blobHeight = radius * 2 * (0.74 + hedgeHashJitter(seed + 5) * 0.42);
      const jitterX = (hedgeHashJitter(seed + 1) - 0.5) * blobRadius * 0.95;
      const jitterY = (hedgeHashJitter(seed + 2) - 0.5) * blobRadius * 0.95;
      const centerZ =
        topZ + blobHeight * 0.5 + hedgeHashJitter(seed + 7) * height * 0.025;

      instances.push({
        center: [x + jitterX, y + jitterY, centerZ],
        height: blobHeight,
        radiusX: radius,
        radiusY: radius * (0.9 + hedgeHashJitter(seed + 11) * 0.14),
      });
    }
    row += 1;
  }

  return instances;
}

function createCapsuleBushFootprint(
  width: number,
  depth: number,
  scale: number,
): BushFoliageFootprint {
  const halfWidth = (width * scale) / 2;
  const halfDepth = (depth * scale) / 2;

  return {
    halfWidth,
    halfDepth,
    getPerimeterPoint: (t, ringScale) =>
      getCapsulePerimeterPoint(t, halfWidth * ringScale, halfDepth * ringScale),
    getOutwardNormal: (x, y) => {
      const normalX = x / (halfWidth || 1);
      const normalY = y / (halfDepth || 1);
      const length = Math.hypot(normalX, normalY) || 1;
      return [normalX / length, normalY / length];
    },
    isInside: (x, y, inset) =>
      isInsideCapsule(x, y, halfWidth, halfDepth, inset),
  };
}

function createEllipseBushFootprint(
  width: number,
  depth: number,
  scale: number,
): BushFoliageFootprint {
  const halfWidth = (width * scale) / 2;
  const halfDepth = (depth * scale) / 2;

  return {
    halfWidth,
    halfDepth,
    getPerimeterPoint: (t, ringScale) =>
      getEllipsePerimeterPoint(t, halfWidth * ringScale, halfDepth * ringScale),
    getOutwardNormal: (x, y) => {
      const normalX = x / (halfWidth * halfWidth || 1);
      const normalY = y / (halfDepth * halfDepth || 1);
      const length = Math.hypot(normalX, normalY) || 1;
      return [normalX / length, normalY / length];
    },
    isInside: (x, y, inset) =>
      isInsideEllipse(x, y, halfWidth, halfDepth, inset),
  };
}

function createPolygonBushFootprint(
  points: [number, number][],
): BushFoliageFootprint {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  points.forEach(([x, y]) => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  return {
    halfWidth: Math.max((maxX - minX) / 2, MIN_SIZE),
    halfDepth: Math.max((maxY - minY) / 2, MIN_SIZE),
    getPerimeterPoint: (t, ringScale) => {
      const [px, py] = getPolygonPerimeterPoint(t, points);
      return [px * ringScale, py * ringScale];
    },
    getOutwardNormal: (x, y) => {
      const length = Math.hypot(x, y) || 1;
      return [x / length, y / length];
    },
    isInside: (x, y, inset) => {
      if (inset <= 0) {
        return isInsidePolygon(x, y, points);
      }

      const centerX = points.reduce((sum, [px]) => sum + px, 0) / points.length;
      const centerY =
        points.reduce((sum, [, py]) => sum + py, 0) / points.length;
      const shrunk = points.map(([px, py]) => {
        const dx = px - centerX;
        const dy = py - centerY;
        const length = Math.hypot(dx, dy) || 1;
        const shrink = Math.max(1 - inset / length, 0.35);
        return [centerX + dx * shrink, centerY + dy * shrink] as [
          number,
          number,
        ];
      });
      return isInsidePolygon(x, y, shrunk);
    },
  };
}

/** Round bush with a dark core and foliage clusters on the shell and top. */
function createRoundBushParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const coreHeight = height * 0.86;
  const coreZ = baseElevation + height * 0.07;
  const blobRadius = width * 0.014;
  const footprint = createEllipseBushFootprint(width, depth, 0.9);
  const foliageInstances = [
    ...collectBushShellInstances(height, baseElevation, blobRadius, footprint, {
      seedOffset: 200,
      pointsPerRing: 36,
    }),
    ...collectBushTopInstances(
      height,
      baseElevation,
      blobRadius,
      footprint,
      400,
    ),
  ];

  return [
    extrudedShape(
      "bush-boundary",
      createScallopedOvalOutline(width, depth, 0.72, 18, 0.06),
      coreZ,
      coreHeight,
      "botanicalFoliageDeep",
      false,
    ),
    ...assembleInstancedBushFoliage(foliageInstances, "bush-foliage", {
      2: "bush-north",
      4: "bush-west",
    }),
  ];
}

/** Organic bush with a dark core and foliage clusters on the shell and top. */
function createOrganicBushParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const coreHeight = height * 0.86;
  const coreZ = baseElevation + height * 0.07;
  const blobRadius = width * 0.0115;
  const outline = createOrganicOutline(width, depth, 0.9);
  const footprint = createPolygonBushFootprint(outline);
  const foliageInstances = [
    ...collectBushShellInstances(height, baseElevation, blobRadius, footprint, {
      seedOffset: 600,
      pointsPerRing: 40,
    }),
    ...collectBushTopInstances(
      height,
      baseElevation,
      blobRadius,
      footprint,
      800,
    ),
  ];

  return [
    extrudedShape(
      "organic-canopy",
      createOrganicOutline(width, depth, 0.72),
      coreZ,
      coreHeight,
      "botanicalFoliageDeep",
      false,
    ),
    cylinder(
      "organic-canopy-lift",
      [width * 0.05, -depth * 0.02, coreZ + coreHeight * 0.72],
      height * 0.22,
      width * 0.08,
      depth * 0.07,
      8,
      "botanicalFoliage",
      false,
      0.16,
    ),
    ...assembleInstancedBushFoliage(foliageInstances, "organic-foliage"),
  ];
}

/** Solid interior core plus perimeter foliage clusters for hedge bushes. */
function createHedgeParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const coreHeight = height * 0.86;
  const coreZ = baseElevation + height * 0.07;
  const blobRadius = width * 0.0085;
  const footprint = createCapsuleBushFootprint(width, depth, 0.9);
  const foliageInstances = [
    ...collectBushShellInstances(height, baseElevation, blobRadius, footprint, {
      seedOffset: 0,
      pointsPerRing: 44,
    }),
    ...collectBushTopInstances(
      height,
      baseElevation,
      blobRadius,
      footprint,
      1000,
    ),
  ];

  return [
    box(
      "hedge-boundary",
      [0, 0, coreZ + coreHeight / 2],
      [width * 0.7, depth * 0.74, coreHeight],
      "botanicalFoliageDeep",
      false,
    ),
    ...assembleInstancedBushFoliage(foliageInstances, "hedge-foliage", {
      2: "hedge-lobe-2",
      4: "hedge-lobe-4",
    }),
  ];
}

function createBushParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  if (modelKey === "botanical:bush-hedge") {
    return createHedgeParts(width, depth, height, baseElevation);
  }

  if (modelKey === "botanical:bush-organic") {
    return createOrganicBushParts(width, depth, height, baseElevation);
  }

  if (modelKey === "botanical:bush-round") {
    return createRoundBushParts(width, depth, height, baseElevation);
  }

  return createRoundBushParts(width, depth, height, baseElevation);
}

function createLeafOutline(
  centerX: number,
  centerY: number,
  length: number,
  width: number,
  rotation: number,
): [number, number][] {
  const points: [number, number][] = [];
  const segments = 12;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  for (let index = 0; index <= segments; index += 1) {
    const t = index / segments;
    const x = (t - 0.5) * length;
    const y = Math.sin(t * Math.PI) * width;
    points.push(rotatePoint(centerX, centerY, x, y, cos, sin));
  }

  for (let index = segments; index >= 0; index -= 1) {
    const t = index / segments;
    const x = (t - 0.5) * length;
    const y = -Math.sin(t * Math.PI) * width;
    points.push(rotatePoint(centerX, centerY, x, y, cos, sin));
  }

  return points;
}

function createScallopedOvalOutline(
  width: number,
  depth: number,
  scale: number,
  segments: number,
  amplitude: number,
): [number, number][] {
  const points: [number, number][] = [];
  const halfWidth = (width * scale) / 2;
  const halfDepth = (depth * scale) / 2;
  const lobes = Math.max(6, Math.round(segments / 2));

  for (let index = 0; index < segments; index += 1) {
    const angle = (index / segments) * Math.PI * 2;
    const scallop = 1 + Math.sin(angle * lobes) * amplitude;
    points.push([
      Math.cos(angle) * halfWidth * scallop,
      Math.sin(angle) * halfDepth * scallop,
    ]);
  }

  return points;
}

function createScallopedCapsuleOutline(
  width: number,
  depth: number,
  scale: number,
  lobes: number,
): [number, number][] {
  const segments = Math.max(lobes * 6, 36);
  const halfWidth = (width * scale) / 2;
  const halfDepth = (depth * scale) / 2;
  const radius = Math.min(halfDepth, halfWidth);
  const straightHalfWidth = Math.max(halfWidth - radius, 0);
  const points: [number, number][] = [];

  for (let index = 0; index <= segments / 2; index += 1) {
    const t = index / (segments / 2);
    const angle = Math.PI / 2 - t * Math.PI;
    const x = straightHalfWidth + Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const scallop = 1 + Math.sin(t * lobes * Math.PI) * 0.07;
    points.push([x, y * scallop]);
  }

  for (let index = 0; index <= segments / 2; index += 1) {
    const t = index / (segments / 2);
    const angle = -Math.PI / 2 - t * Math.PI;
    const x = -straightHalfWidth + Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    const scallop = 1 + Math.sin(t * lobes * Math.PI) * 0.07;
    points.push([x, y * scallop]);
  }

  return points;
}

function rotatePoint(
  centerX: number,
  centerY: number,
  x: number,
  y: number,
  cos: number,
  sin: number,
): [number, number] {
  return [centerX + x * cos - y * sin, centerY + x * sin + y * cos];
}

/** Rotates primitive part footprints to align with the 2D symbol orientation. */
function rotateLivingRoomPartsInPlan(
  parts: LivingRoom3DPart[],
  rotationZ: number,
): LivingRoom3DPart[] {
  const cos = Math.cos(rotationZ);
  const sin = Math.sin(rotationZ);
  const rotatePlanPoint = (x: number, y: number): [number, number] => [
    x * cos - y * sin,
    x * sin + y * cos,
  ];
  const rotateCenter = (
    center: [number, number, number],
  ): [number, number, number] => {
    const [x, y, z] = center;
    const [rotatedX, rotatedY] = rotatePlanPoint(x, y);
    return [rotatedX, rotatedY, z];
  };

  return parts.map((part) => {
    if (part.type === "instancedCylinders") {
      return {
        ...part,
        instances: part.instances.map((instance) => ({
          ...instance,
          center: rotateCenter(instance.center),
          rotationZ: (instance.rotationZ ?? 0) + rotationZ,
        })),
      };
    }

    if (part.type === "extrudedShape") {
      return {
        ...part,
        center: rotateCenter(part.center),
        points: part.points.map(([x, y]) => rotatePlanPoint(x, y)),
        holes: part.holes?.map((hole) =>
          hole.map(([x, y]) => rotatePlanPoint(x, y)),
        ),
        rotationZ: (part.rotationZ ?? 0) + rotationZ,
      };
    }

    if (part.type === "slopedBasin") {
      return {
        ...part,
        center: rotateCenter(part.center),
        topPoints: part.topPoints.map(([x, y]) => rotatePlanPoint(x, y)),
        bottomPoints: part.bottomPoints.map(([x, y]) => rotatePlanPoint(x, y)),
      };
    }

    if (
      part.type === "box" ||
      part.type === "cylinder" ||
      part.type === "pillow"
    ) {
      return {
        ...part,
        center: rotateCenter(part.center),
        rotationZ: (part.rotationZ ?? 0) + rotationZ,
      };
    }

    if (part.type === "ellipsoid") {
      return {
        ...part,
        center: rotateCenter(part.center),
        rotationZ: (part.rotationZ ?? 0) + rotationZ,
      };
    }

    return part;
  });
}

/**
 * Stock bed: a mattress on four round legs, with a duvet and pillows. No frame,
 * headboard or foot rail - those belong to the styled catalog models, not the
 * plain library symbol. King and single beds share every ratio so the two
 * symbols read as the same piece of furniture at different widths.
 *
 * `along` runs from the head (negative) to the foot (positive), `across` runs
 * between the long sides.
 */
/** Leg radius keys off the bed height so both beds get identical legs. */
const BED_LEG_RADIUS_RATIO = 0.035;
const BED_LEG_ALONG_INSET_RATIO = 0.04;
const BED_LEG_ACROSS_INSET_RATIO = 0.06;
const BED_LEG_RADIAL_SEGMENTS = 16;
const BED_MATTRESS_HEIGHT_RATIO = 0.42;
/** Duvet covers most of the mattress from the foot and folds back at the head. */
const BED_DUVET_ALONG_RATIO = 0.72;
const BED_DUVET_ACROSS_RATIO = 0.97;
const BED_DUVET_HEIGHT_RATIO = 0.1;
const BED_DUVET_FOOT_GAP_RATIO = 0.01;
const BED_DUVET_FOLD_ALONG_RATIO = 0.1;
const BED_DUVET_FOLD_HEIGHT_RATIO = 0.05;
/** Cushioned edge softness: mattress stays firm, duvet and fold are softer. */
const BED_MATTRESS_SOFTNESS = 0.45;
const BED_DUVET_SOFTNESS = 0.7;
const BED_DUVET_FOLD_SOFTNESS = 0.8;
/** Bed pillows keep a rectangular envelope with a smooth, tapered fabric crown. */
const BED_PILLOW_SHAPE: PillowShapeOptions = {
  outlineExponent: 7,
  rimRatio: 0.26,
  puffExponent: 0.65,
  wrinkleDepthRatio: 0,
  smoothSeams: true,
};
/** Legs keep a neutral grey material so they ignore the symbol fill colour. */
const BED_LEG_MATERIAL_SLOT: FloorPlan3DMaterialSlot = "furnitureMetal";
const BED_PILLOW_ALONG_RATIO = 0.2;
/** 150 mm at the stock bed size, relative to its preserved 625 mm envelope. */
const BED_PILLOW_HEIGHT_RATIO = 0.24;
/** Let the pillow settle 30 mm into the mattress at its 150 mm stock height. */
const BED_PILLOW_SINK_RATIO = 0.2;
const BED_PILLOW_HEAD_INSET_RATIO = 0.03;
const BED_KING_PILLOW_ACROSS_RATIO = 0.42;
const BED_SINGLE_PILLOW_ACROSS_RATIO = 0.66;
/** Bedding sinks slightly into whatever it rests on so no faces end up coplanar. */
const BED_BEDDING_SINK_RATIO = 0.25;

function createBedParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  // The 2D symbol always draws the bed head-to-foot along X with the pillows at
  // -X, so the 3D model is laid out the same way for every footprint. Picking
  // the axis from the footprint proportions instead flips nearly square
  // footprints (a king bed is 2100 x 1800) 90 degrees away from the plan.
  const bedLength = width;
  const bedWidth = depth;
  const isKing = modelKey === "bedroom:bed-king";
  const pillowCount = isKing ? 2 : 1;
  const authoredHeight = getScaledPrimitiveSymbol3DAuthoredHeight(
    modelKey,
    height,
  );

  const mattressHeight = authoredHeight * BED_MATTRESS_HEIGHT_RATIO;
  const pillowLength = bedLength * BED_PILLOW_ALONG_RATIO;
  const pillowWidth =
    bedWidth *
    (isKing ? BED_KING_PILLOW_ACROSS_RATIO : BED_SINGLE_PILLOW_ACROSS_RATIO);
  const pillowHeight = authoredHeight * BED_PILLOW_HEIGHT_RATIO;
  const mattressSurfaceHeight =
    getScaledPrimitiveSymbol3DReferenceHeight(modelKey, height) ??
    height - pillowHeight * (1 - BED_PILLOW_SINK_RATIO);
  // The reference height is the top of the mattress, while the overall symbol
  // height still includes the pillows resting above it.
  const mattressTopZ = baseElevation + mattressSurfaceHeight;
  const mattressCenterZ = mattressTopZ - mattressHeight / 2;
  const legHeight = Math.max(
    mattressCenterZ - mattressHeight / 2 - baseElevation,
    0,
  );
  const legRadius = authoredHeight * BED_LEG_RADIUS_RATIO;
  const duvetHeight = authoredHeight * BED_DUVET_HEIGHT_RATIO;
  const duvetLength = bedLength * BED_DUVET_ALONG_RATIO;
  const duvetFootEdge = bedLength * (0.5 - BED_DUVET_FOOT_GAP_RATIO);
  const duvetCenterAlong = duvetFootEdge - duvetLength / 2;
  const foldLength = bedLength * BED_DUVET_FOLD_ALONG_RATIO;
  const foldHeight = authoredHeight * BED_DUVET_FOLD_HEIGHT_RATIO;

  /** Centre height for bedding resting on `surfaceZ`, sunk in to avoid z-fighting. */
  const beddingCenterZ = (surfaceZ: number, partHeight: number) =>
    surfaceZ + partHeight * (0.5 - BED_BEDDING_SINK_RATIO);
  const pillowCenterZ =
    mattressTopZ + pillowHeight * (0.5 - BED_PILLOW_SINK_RATIO);

  const beddingBox = (
    id: string,
    along: number,
    alongSize: number,
    acrossSize: number,
    partHeight: number,
    softness: number,
    surfaceZ = mattressTopZ,
  ): LivingRoom3DPart => ({
    ...box(
      id,
      [along, 0, beddingCenterZ(surfaceZ, partHeight)],
      [alongSize, acrossSize, partHeight],
      "furnitureFabric",
      true,
    ),
    softness,
  });

  const duvetTopZ = mattressTopZ + duvetHeight * (1 - BED_BEDDING_SINK_RATIO);

  const parts: LivingRoom3DPart[] = [
    {
      ...box(
        "mattress",
        [0, 0, mattressCenterZ],
        [bedLength, bedWidth, mattressHeight],
        "furnitureFabric",
        true,
      ),
      softness: BED_MATTRESS_SOFTNESS,
    },
    beddingBox(
      "duvet",
      duvetCenterAlong,
      duvetLength,
      bedWidth * BED_DUVET_ACROSS_RATIO,
      duvetHeight,
      BED_DUVET_SOFTNESS,
    ),
    // The fold is the turned-down head edge of the duvet: like the 2D symbol it
    // lies on top of the duvet and points toward the foot, not toward the pillows.
    beddingBox(
      "duvet-fold",
      duvetCenterAlong - duvetLength / 2 + foldLength / 2,
      foldLength,
      bedWidth * BED_DUVET_ACROSS_RATIO,
      foldHeight,
      BED_DUVET_FOLD_SOFTNESS,
      duvetTopZ,
    ),
  ];

  if (legHeight > MIN_SIZE) {
    const legAlong =
      bedLength / 2 - bedLength * BED_LEG_ALONG_INSET_RATIO - legRadius;
    const legAcross =
      bedWidth / 2 - bedWidth * BED_LEG_ACROSS_INSET_RATIO - legRadius;
    [-1, 1].forEach((alongSign) => {
      [-1, 1].forEach((acrossSign) => {
        parts.push({
          ...cylinder(
            `leg-${alongSign < 0 ? "head" : "foot"}-${acrossSign < 0 ? "left" : "right"}`,
            [
              legAlong * alongSign,
              legAcross * acrossSign,
              baseElevation + legHeight / 2,
            ],
            legHeight,
            legRadius,
            legRadius,
            BED_LEG_RADIAL_SEGMENTS,
            BED_LEG_MATERIAL_SLOT,
          ),
          // Tinting the symbol recolours the bedding, never the legs.
          ignoreSymbolFillColor: true,
        });
      });
    });
  }

  const pillowAlong =
    -(bedLength / 2) +
    bedLength * BED_PILLOW_HEAD_INSET_RATIO +
    pillowLength / 2;
  const pillowSpacing = pillowWidth * 1.06;
  for (let index = 0; index < pillowCount; index += 1) {
    const across =
      pillowCount === 1 ? 0 : (index - (pillowCount - 1) / 2) * pillowSpacing;
    parts.push(
      pillow(
        `pillow-${index}`,
        [pillowAlong, across, pillowCenterZ],
        [pillowLength, pillowWidth, pillowHeight],
        "furnitureFabric",
        { useFillColor: true, shape: BED_PILLOW_SHAPE },
      ),
    );
  }

  return parts;
}

function createBathroomParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  if (modelKey === "bathroom:bathtub") {
    return createBathtubParts(width, depth, height, baseElevation);
  }
  if (modelKey === "bathroom:shower") {
    return createShowerParts(width, depth, height, baseElevation);
  }
  if (modelKey === "bathroom:toilet") {
    return createDetailedToiletParts(width, depth, height, baseElevation);
  }
  return createBathroomBasinParts(width, depth, height, baseElevation);
}

/** Detailed close-coupled toilet with a raised lid and recessed ceramic bowl. */
function createDetailedToiletParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const scale = height / 0.95;
  const ceramicMaterial = "furnitureGlass" as const;
  const seatTopZ =
    baseElevation +
    (getScaledPrimitiveSymbol3DReferenceHeight("bathroom:toilet", height) ??
      height * (0.48 / 0.95));
  const seatThickness = 0.018 * scale;
  const seatBottomZ = seatTopZ - seatThickness;
  const seatCenterX = width * 0.13;
  const seatLength = width * 0.74;
  const seatWidth = depth * 0.78;
  const seatOuter = createEllipseOutline(
    seatCenterX,
    0,
    seatLength,
    seatWidth,
    48,
  );
  const seatOpening = createEllipseOutline(
    seatCenterX + width * 0.015,
    0,
    seatLength * 0.72,
    seatWidth * 0.7,
    48,
  );
  const basinOpening = seatOpening;
  const bowlFloorOutline = createEllipseOutline(
    seatCenterX + width * 0.025,
    0,
    seatLength * 0.24,
    seatWidth * 0.25,
    48,
  );
  const bowlBottomZ = baseElevation + 0.245 * scale;
  const bowlBottomOutline = createEllipseOutline(
    seatCenterX - width * 0.1,
    0,
    seatLength * 0.55,
    seatWidth * 0.67,
    48,
  );
  const bowlFloorZ = bowlBottomZ + 0.065 * scale;
  const tankBackX = -width / 2 + width * 0.025;
  const tankLength = width * 0.23;
  const tankFrontX = tankBackX + tankLength;
  const tankCenterX = tankBackX + tankLength / 2;
  const lidLength = seatLength * 0.84;
  const lidWidth = seatWidth * 0.88;
  const lidThickness = 0.014 * scale;
  // With the lid raised, its extrusion depth points toward -X. Place the hinge
  // far enough in front of the tank that even the lid's rear face has clearance.
  const hingeX = tankFrontX + lidThickness + 0.006 * scale;
  const lidHingeZ = seatTopZ + 0.004 * scale;
  const lidOutline = createEllipseOutline(
    lidLength / 2,
    0,
    lidLength,
    lidWidth,
    48,
  );
  const tankBottomZ = baseElevation + 0.38 * scale;
  const tankBodyHeight = 0.5 * scale;
  const tankTopZ = tankBottomZ + tankBodyHeight;
  const tankDepth = depth * 0.88;
  const pedestalTopZ = baseElevation + 0.3 * scale;
  const pedestalBottomZ = baseElevation + 0.045 * scale;
  const pedestalTopOutline = createCapsuleOutline(
    width * 0.34,
    depth * 0.54,
    1,
    -width * 0.02,
    0,
    24,
  );
  const pedestalBottomOutline = createCapsuleOutline(
    width * 0.42,
    depth * 0.64,
    1,
    -width * 0.08,
    0,
    24,
  );

  const seat = {
    ...extrudedShape(
      "seat",
      seatOuter,
      seatBottomZ,
      seatThickness,
      ceramicMaterial,
      false,
      undefined,
      [seatOpening],
    ),
    height: seatThickness,
    bevelThickness: 0.003 * scale,
    bevelSize: 0.004 * scale,
    bevelSegments: 3,
    curveSegments: 16,
  } satisfies Extract<LivingRoom3DPart, { type: "extrudedShape" }>;

  const raisedLid = {
    ...extrudedShape(
      "raised-lid",
      lidOutline,
      lidHingeZ,
      lidThickness,
      ceramicMaterial,
    ),
    height: lidThickness,
    rotationY: -Math.PI / 2,
    bevelThickness: 0.004 * scale,
    bevelSize: 0.006 * scale,
    bevelSegments: 3,
    curveSegments: 16,
    center: [hingeX, 0, lidHingeZ] as [number, number, number],
  } satisfies Extract<LivingRoom3DPart, { type: "extrudedShape" }>;

  const tank = {
    ...box(
      "tank",
      [tankCenterX, 0, tankBottomZ + tankBodyHeight / 2],
      [tankLength, tankDepth, tankBodyHeight],
      ceramicMaterial,
    ),
    softness: 0.72,
  } satisfies Extract<LivingRoom3DPart, { type: "box" }>;
  const pedestalFoot = {
    ...extrudedShape(
      "pedestal-foot",
      createCapsuleOutline(width * 0.43, depth * 0.65, 1, -width * 0.08, 0, 24),
      baseElevation,
      0.05 * scale,
      ceramicMaterial,
    ),
    height: 0.05 * scale,
    bevelThickness: 0.006 * scale,
    bevelSize: 0.008 * scale,
    bevelSegments: 3,
    curveSegments: 16,
  } satisfies Extract<LivingRoom3DPart, { type: "extrudedShape" }>;

  const parts: LivingRoom3DPart[] = [
    pedestalFoot,
    {
      id: "pedestal-shell",
      type: "slopedBasin",
      center: [0, 0, 0],
      topPoints: pedestalTopOutline,
      bottomPoints: pedestalBottomOutline,
      topZ: pedestalTopZ,
      bottomZ: pedestalBottomZ,
      verticalSegments: 12,
      materialSlot: ceramicMaterial,
    },
    {
      id: "bowl",
      type: "slopedBasin",
      center: [0, 0, 0],
      topPoints: seatOuter,
      bottomPoints: bowlBottomOutline,
      topZ: seatBottomZ - 0.002 * scale,
      bottomZ: bowlBottomZ,
      verticalSegments: 16,
      materialSlot: ceramicMaterial,
    },
    {
      id: "inner-bowl",
      type: "slopedBasin",
      center: [0, 0, 0],
      topPoints: basinOpening,
      bottomPoints: bowlFloorOutline,
      topZ: seatBottomZ - 0.004 * scale,
      bottomZ: bowlFloorZ,
      verticalSegments: 12,
      materialSlot: ceramicMaterial,
    },
    {
      ...extrudedShape(
        "bowl-rim",
        seatOuter,
        seatBottomZ - 0.012 * scale,
        0.012 * scale,
        ceramicMaterial,
        false,
        undefined,
        [basinOpening],
      ),
      height: 0.012 * scale,
      bevelThickness: 0.002 * scale,
      bevelSize: 0.002 * scale,
      bevelSegments: 2,
    },
    {
      ...extrudedShape(
        "bowl-floor",
        bowlFloorOutline,
        bowlFloorZ - 0.012 * scale,
        0.012 * scale,
        ceramicMaterial,
      ),
      height: 0.012 * scale,
      bevelThickness: 0.002 * scale,
      bevelSize: 0.003 * scale,
      bevelSegments: 2,
    },
    seat,
    tank,
    raisedLid,
    cylinder(
      "flush-button",
      [tankCenterX, 0, tankTopZ + 0.005 * scale],
      0.01 * scale,
      depth * 0.065,
      depth * 0.065,
      32,
      "furnitureMetal",
      false,
      undefined,
      0.003,
    ),
  ];

  [-1, 1].forEach((side) => {
    parts.push(
      {
        ...box(
          `lid-hinge-${side < 0 ? "left" : "right"}`,
          [hingeX, side * seatWidth * 0.28, lidHingeZ],
          [0.045 * scale, 0.055 * scale, 0.025 * scale],
          "doorHardware",
        ),
        softness: 0.75,
      },
      {
        ...cylinder(
          `mounting-cap-${side < 0 ? "left" : "right"}`,
          [-width * 0.055, side * depth * 0.325, baseElevation + 0.085 * scale],
          0.012 * scale,
          0.017 * scale,
          0.017 * scale,
          20,
          "furnitureMetal",
          false,
          undefined,
          0.003,
        ),
        rotationX: -Math.PI / 2,
      },
    );
  });

  return parts;
}

/** Open modern shower with a ceiling rain head and compact wall-side controls. */
function createShowerParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const footprintMin = Math.min(width, depth);
  const ceilingZ = baseElevation + height;
  const trayHeight = height * 0.012;
  const trayTopZ = baseElevation + trayHeight;
  const fixtureY = depth * 0.4;

  const rainHeadRadius = footprintMin * 0.18;
  const rainHeadThickness = height * 0.012;
  const rainDropLength = height * 0.15;
  const rainHeadTopZ = ceilingZ - rainDropLength;
  const rainPipeRadius = footprintMin * 0.014;

  const controlRadius = footprintMin * 0.045;
  const controlSpacing = width * 0.11;
  const controlZ = baseElevation + height * 0.43;
  const controlDepth = depth * 0.05;

  const handShowerX = -width * 0.3;
  const handShowerBottomZ = baseElevation + height * 0.54;
  const handShowerTopZ = baseElevation + height * 0.69;
  const hoseY = fixtureY - depth * 0.03;
  const hoseRadius = footprintMin * 0.008;
  const hosePoints: [number, number, number][] = [
    [handShowerX, hoseY, handShowerBottomZ],
    [handShowerX - width * 0.025, hoseY, baseElevation + height * 0.42],
    [handShowerX - width * 0.015, hoseY, baseElevation + height * 0.25],
    [handShowerX + width * 0.035, hoseY, baseElevation + height * 0.18],
    [handShowerX + width * 0.12, hoseY, baseElevation + height * 0.17],
    [-controlSpacing * 1.15, hoseY, baseElevation + height * 0.27],
    [-controlSpacing, hoseY, controlZ],
  ];

  const parts: LivingRoom3DPart[] = [
    box(
      "tray",
      [0, 0, baseElevation + trayHeight / 2],
      [width * 0.96, depth * 0.96, trayHeight],
      "doorPanel",
      false,
      undefined,
      Math.min(width * 0.96, depth * 0.96, trayHeight),
    ),
    cylinder(
      "drain",
      [width * 0.3, -depth * 0.3, trayTopZ + height * 0.002],
      height * 0.004,
      footprintMin * 0.035,
      footprintMin * 0.035,
      24,
      "furnitureMetal",
      false,
      undefined,
      Math.min(height * 0.004, footprintMin * 0.035),
    ),
    cylinder(
      "ceiling-mount",
      [0, 0, ceilingZ - height * 0.005],
      height * 0.01,
      footprintMin * 0.04,
      footprintMin * 0.04,
      32,
      "furnitureMetal",
      false,
      undefined,
      Math.min(height * 0.01, footprintMin * 0.04),
    ),
    cylinder(
      "rain-head-drop",
      [0, 0, ceilingZ - rainDropLength / 2],
      rainDropLength,
      rainPipeRadius,
      rainPipeRadius,
      20,
      "furnitureMetal",
      false,
      undefined,
      Math.min(rainDropLength, rainPipeRadius),
    ),
    cylinder(
      "rain-head",
      [0, 0, rainHeadTopZ - rainHeadThickness / 2],
      rainHeadThickness,
      rainHeadRadius,
      rainHeadRadius,
      48,
      "furnitureMetal",
      false,
      undefined,
      Math.min(rainHeadThickness, rainHeadRadius),
    ),
  ];

  [-controlSpacing, controlSpacing].forEach((x, index) => {
    const side = index === 0 ? "left" : "right";
    parts.push(
      {
        ...cylinder(
          `mixer-trim-${side}`,
          [x, fixtureY + depth * 0.012, controlZ],
          depth * 0.018,
          controlRadius * 1.35,
          controlRadius * 1.35,
          32,
          "doorHardware",
          false,
          undefined,
          Math.min(depth * 0.018, controlRadius * 1.35),
        ),
        rotationX: -Math.PI / 2,
      },
      {
        ...cylinder(
          `mixer-control-${side}`,
          [x, fixtureY - controlDepth * 0.2, controlZ],
          controlDepth,
          controlRadius,
          controlRadius,
          32,
          "furnitureMetal",
          false,
          undefined,
          Math.min(controlDepth, controlRadius),
        ),
        rotationX: -Math.PI / 2,
      },
    );
  });

  parts.push(
    cylinderBetweenXZ(
      "hand-shower-handle",
      [handShowerX, fixtureY - depth * 0.03, handShowerBottomZ],
      [handShowerX + width * 0.025, fixtureY - depth * 0.03, handShowerTopZ],
      footprintMin * 0.025,
      24,
      "furnitureMetal",
    ),
    {
      ...cylinder(
        "hand-shower-head",
        [handShowerX + width * 0.025, fixtureY - depth * 0.045, handShowerTopZ],
        depth * 0.035,
        footprintMin * 0.06,
        footprintMin * 0.06,
        32,
        "furnitureMetal",
        false,
        undefined,
        Math.min(depth * 0.035, footprintMin * 0.06),
      ),
      rotationX: -Math.PI / 2,
    },
    {
      ...cylinder(
        "hand-shower-holder",
        [handShowerX, fixtureY, baseElevation + height * 0.62],
        depth * 0.05,
        footprintMin * 0.035,
        footprintMin * 0.035,
        24,
        "doorHardware",
        false,
        undefined,
        Math.min(depth * 0.05, footprintMin * 0.035),
      ),
      rotationX: -Math.PI / 2,
    },
  );

  hosePoints.slice(1).forEach((point, index) => {
    parts.push(
      cylinderBetweenXZ(
        `hand-shower-hose-${index}`,
        hosePoints[index],
        point,
        hoseRadius,
        12,
        "furnitureMetal",
      ),
    );
  });

  return parts;
}

/** Thin cylinder joining two points in an X/Z plane, used for curved fixture runs. */
function cylinderBetweenXZ(
  id: string,
  start: [number, number, number],
  end: [number, number, number],
  radius: number,
  radialSegments: number,
  materialSlot: FloorPlan3DMaterialSlot,
): LivingRoom3DPart {
  const deltaX = end[0] - start[0];
  const deltaZ = end[2] - start[2];
  const length = Math.hypot(deltaX, deltaZ);

  return {
    ...cylinder(
      id,
      [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
        (start[2] + end[2]) / 2,
      ],
      length,
      radius,
      radius,
      radialSegments,
      materialSlot,
      false,
      undefined,
      Math.min(radius, 0.004),
    ),
    rotationZ: -Math.atan2(deltaX, deltaZ),
  };
}

/** Open-top cabinet shell with a solid front facade. */
function createBathroomBasinCabinetParts(
  width: number,
  depth: number,
  bodyBottomZ: number,
  bodyHeight: number,
): LivingRoom3DPart[] {
  const panelThickness = Math.max(Math.min(width * 0.055, depth * 0.08), 0.02);
  const shelfHeight = Math.max(bodyHeight * 0.035, MIN_SIZE);
  const bodyCenterZ = bodyBottomZ + bodyHeight / 2;
  const innerWidth = Math.max(width - panelThickness * 2, MIN_SIZE);
  const innerDepth = Math.max(depth - panelThickness, MIN_SIZE);
  const frontCenterY = -depth / 2 + panelThickness / 2;
  const cabinetMaterial = "doorHardware" as const;

  return [
    box(
      "vanity-left",
      [-width / 2 + panelThickness / 2, 0, bodyCenterZ],
      [panelThickness, depth, bodyHeight],
      cabinetMaterial,
    ),
    box(
      "vanity-right",
      [width / 2 - panelThickness / 2, 0, bodyCenterZ],
      [panelThickness, depth, bodyHeight],
      cabinetMaterial,
    ),
    box(
      "vanity-back",
      [0, depth / 2 - panelThickness / 2, bodyCenterZ],
      [innerWidth, panelThickness, bodyHeight],
      cabinetMaterial,
    ),
    box(
      "vanity-bottom",
      [0, 0, bodyBottomZ + shelfHeight / 2],
      [innerWidth, innerDepth, shelfHeight],
      cabinetMaterial,
    ),
    box(
      "vanity-front",
      [0, frontCenterY, bodyCenterZ],
      [width, panelThickness, bodyHeight],
      cabinetMaterial,
    ),
  ];
}

/** Vanity basin with recessed bowl and faucet over a base cabinet. */
function createBathroomBasinParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const deckHeight = Math.max(height * 0.06, MIN_SIZE);
  const bodyBottomZ = baseElevation;
  const basinSurfaceHeight =
    getScaledPrimitiveSymbol3DReferenceHeight("bathroom:basin", height) ??
    height * 0.82;
  const deckTop = baseElevation + basinSurfaceHeight;
  const deckZ = deckTop - deckHeight;
  const bodyHeight = Math.max(deckZ - bodyBottomZ, MIN_SIZE);
  // A washbasin is shallower than a kitchen sink, particularly within this
  // compact opening; keeping it near 100 mm avoids the deep bucket appearance.
  const basinDepth = Math.max(height * 0.105, MIN_SIZE);
  const basinFloorHeight = Math.max(height * 0.025, MIN_SIZE);
  const basinFloorZ = deckTop - basinDepth;
  const wallHeight = basinDepth - basinFloorHeight;
  const wallZ = basinFloorZ + basinFloorHeight;
  const bowlWidth = width * 0.58;
  const bowlDepth = depth * PORCELAIN_SINK_BOWL_DEPTH_RATIO;
  const { opening, inner } = createPorcelainBowlOutlines(
    0,
    0,
    bowlWidth,
    bowlDepth,
  );
  const porcelainMaterial = "furnitureGlass" as const;
  const deckOutline = createRoundedRectOutline(
    0,
    0,
    width,
    depth,
    Math.min(depth * 0.035, width * 0.025),
    6,
  );

  return rotateLivingRoomPartsInPlan(
    [
      ...createBathroomBasinCabinetParts(width, depth, bodyBottomZ, bodyHeight),
      flatPorcelainExtrusion("sink-deck", deckOutline, deckZ, deckHeight, [
        opening,
      ]),
      {
        ...flatPorcelainExtrusion(
          "sink-basin-wall",
          opening,
          wallZ,
          wallHeight,
          [inner],
        ),
        materialSlot: porcelainMaterial,
        polygonOffset: true,
        polygonOffsetFactor: SINK_SURFACE_DEPTH_BIAS,
      },
      {
        ...flatPorcelainFloorExtrusion(
          "sink-basin-floor",
          inner,
          basinFloorZ,
          basinFloorHeight,
        ),
        materialSlot: porcelainMaterial,
        polygonOffset: true,
        polygonOffsetFactor: SINK_SURFACE_DEPTH_BIAS,
      },
      ...createFaucetParts(
        width,
        depth,
        height,
        deckTop,
        PORCELAIN_SINK_FAUCET_OPTIONS,
      ),
    ],
    Math.PI,
  );
}

function createBathtubParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const sideThickness = Math.min(
    Math.max(depth * 0.09, MIN_SIZE),
    depth * 0.18,
  );
  const endThickness = Math.min(Math.max(width * 0.08, MIN_SIZE), width * 0.16);
  const deckHeight = Math.max(height * 0.12, MIN_SIZE);
  const rimSurfaceHeight =
    getScaledPrimitiveSymbol3DReferenceHeight("bathroom:bathtub", height) ??
    height * 0.84;
  const rimTop = baseElevation + rimSurfaceHeight;
  const rolledRimHeight = Math.max(height * 0.065, MIN_SIZE);
  const deckTop = rimTop - (rolledRimHeight - height * 0.025);
  const deckZ = deckTop - deckHeight;
  const apronHeight = Math.max(deckZ - baseElevation, MIN_SIZE);
  const basinFloorZ = baseElevation + height * 0.22;
  const basinFloorHeight = Math.max(height * 0.055, MIN_SIZE);
  const drainRadius = Math.min(width, depth) * 0.045;
  const drainHeight = 0.004;
  const drainZ = basinFloorZ + basinFloorHeight + drainHeight / 2;
  const deckHole = createCapsuleOutline(width * 0.9, depth * 0.78, 1, 0, 0, 28);
  const basinTopOutline = createCapsuleOutline(
    width * 0.86,
    depth * 0.7,
    1,
    0,
    0,
    28,
  );
  const basinFloorOutline = createCapsuleOutline(
    width * 0.78,
    depth * 0.56,
    1,
    0,
    0,
    28,
  );
  const basinWallTopZ = rimTop - rolledRimHeight;
  const basinWallBottomZ = basinFloorZ + basinFloorHeight;
  const parts: LivingRoom3DPart[] = [
    box(
      "tub-left-apron",
      [0, -depth / 2 + sideThickness / 2, baseElevation + apronHeight / 2],
      [width, sideThickness, apronHeight],
      "doorPanel",
    ),
    box(
      "tub-right-apron",
      [0, depth / 2 - sideThickness / 2, baseElevation + apronHeight / 2],
      [width, sideThickness, apronHeight],
      "doorPanel",
    ),
    box(
      "tub-front-apron",
      [-width / 2 + endThickness / 2, 0, baseElevation + apronHeight / 2],
      [endThickness, depth - sideThickness * 2, apronHeight],
      "doorPanel",
    ),
    box(
      "tub-back-apron",
      [width / 2 - endThickness / 2, 0, baseElevation + apronHeight / 2],
      [endThickness, depth - sideThickness * 2, apronHeight],
      "doorPanel",
    ),
    extrudedShape(
      "tub-deck",
      createRoundedRectOutline(
        0,
        0,
        width,
        depth,
        Math.min(depth * 0.045, width * 0.025),
        8,
      ),
      deckZ,
      deckHeight,
      "doorPanel",
      false,
      undefined,
      [deckHole],
    ),
    extrudedShape(
      "rolled-rim",
      createCapsuleOutline(width * 0.92, depth * 0.78, 1, 0, 0, 28),
      rimTop - rolledRimHeight,
      rolledRimHeight,
      "doorPanel",
      false,
      undefined,
      [createCapsuleOutline(width * 0.86, depth * 0.7, 1, 0, 0, 28)],
    ),
    {
      id: "basin-sloped-wall",
      type: "slopedBasin",
      center: [0, 0, 0],
      topPoints: basinTopOutline,
      bottomPoints: basinFloorOutline,
      topZ: basinWallTopZ,
      bottomZ: basinWallBottomZ,
      verticalSegments: 10,
      materialSlot: "doorPanel",
    },
    extrudedShape(
      "basin-floor",
      basinFloorOutline,
      basinFloorZ,
      basinFloorHeight,
      "doorPanel",
    ),
    cylinder(
      "drain",
      [0, 0, drainZ],
      drainHeight,
      drainRadius,
      drainRadius,
      24,
      "furnitureMetal",
    ),
    ...createFaucetParts(width, depth, height, deckTop),
  ];

  return parts;
}

/** Bathtub-style faucet base and spout, reused on porcelain sinks. */
function createFaucetParts(
  width: number,
  depth: number,
  height: number,
  deckTop: number,
  options?: {
    /** Spout length as a fraction of fixture depth (default 0.12). */
    spoutLengthRatio?: number;
    /** How far the spout center sits toward the bowl from the tap side. */
    spoutReachRatio?: number;
    /** Vertical scale for base and spout height. */
    heightScale?: number;
    /** Center spout on the base stem and stack it on the base top. */
    alignSpoutOnBase?: boolean;
  },
): LivingRoom3DPart[] {
  const spoutLengthRatio = options?.spoutLengthRatio ?? 0.12;
  const spoutReachRatio = options?.spoutReachRatio ?? 0.1;
  const heightScale = options?.heightScale ?? 1;
  const alignSpoutOnBase = options?.alignSpoutOnBase ?? false;
  const tapSideY = depth * 0.38;
  const spoutLength = depth * spoutLengthRatio;
  const baseHeight = Math.max(height * 0.045 * heightScale, 0.03);
  const spoutRise = height * 0.065 * heightScale;
  const spoutThickness = height * 0.035 * heightScale;
  const baseCenterZ = deckTop + baseHeight / 2;
  const baseTopZ = baseCenterZ + baseHeight / 2;
  const spoutCenterY = alignSpoutOnBase
    ? tapSideY - spoutLength / 2
    : tapSideY - depth * spoutReachRatio;
  const spoutCenterZ = alignSpoutOnBase
    ? baseTopZ + spoutThickness / 2
    : deckTop + spoutRise;
  return [
    cylinder(
      "faucet-base",
      [0, tapSideY, baseCenterZ],
      baseHeight,
      Math.max(width * 0.018, 0.012),
      Math.max(depth * 0.032, 0.012),
      16,
      "furnitureMetal",
    ),
    box(
      "faucet-spout",
      [0, spoutCenterY, spoutCenterZ],
      [depth * 0.04, spoutLength, spoutThickness],
      "furnitureMetal",
    ),
  ];
}

type PorcelainSinkBowlSpec = {
  id: string;
  centerX: number;
  centerY?: number;
  widthRatio: number;
  depthRatio: number;
};

const PORCELAIN_SINK_FAUCET_OPTIONS = {
  spoutLengthRatio: 0.34,
  spoutReachRatio: 0,
  heightScale: 1.1,
  alignSpoutOnBase: true,
} as const;

const PORCELAIN_SINK_BOWL_DEPTH_RATIO = 0.78;

function getPorcelainSinkBowlSpecs(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
): PorcelainSinkBowlSpec[] {
  if (modelKey === "kitchen:sink-single") {
    return [
      {
        id: "main",
        centerX: 0,
        widthRatio: 0.64,
        depthRatio: PORCELAIN_SINK_BOWL_DEPTH_RATIO,
      },
    ];
  }
  if (modelKey === "kitchen:sink-double") {
    return [
      {
        id: "left",
        centerX: -width * 0.22,
        widthRatio: 0.28,
        depthRatio: PORCELAIN_SINK_BOWL_DEPTH_RATIO,
      },
      {
        id: "right",
        centerX: width * 0.2,
        widthRatio: 0.28,
        depthRatio: PORCELAIN_SINK_BOWL_DEPTH_RATIO,
      },
    ];
  }
  if (modelKey === "kitchen:sink-drainboard") {
    return [
      {
        id: "main",
        centerX: width * 0.2,
        widthRatio: 0.28,
        depthRatio: PORCELAIN_SINK_BOWL_DEPTH_RATIO,
      },
    ];
  }
  return [
    {
      id: "main",
      centerX: 0,
      widthRatio: 0.64,
      depthRatio: PORCELAIN_SINK_BOWL_DEPTH_RATIO,
    },
  ];
}

function createPorcelainBowlOutlines(
  centerX: number,
  centerY: number,
  bowlWidth: number,
  bowlDepth: number,
): { opening: [number, number][]; inner: [number, number][] } {
  const bowlRadius = Math.min(bowlWidth, bowlDepth) * 0.14;
  return {
    opening: createRoundedRectOutline(
      centerX,
      centerY,
      bowlWidth,
      bowlDepth,
      bowlRadius,
      6,
    ),
    inner: createRoundedRectOutline(
      centerX,
      centerY,
      bowlWidth * 0.9,
      bowlDepth * 0.9,
      bowlRadius * 0.85,
      6,
    ),
  };
}

const SINK_DRAIN_HEIGHT = 0.003;
/** Bowl depth as a fraction of fixture height (~13 cm on a 90 cm cabinet). */
const PORCELAIN_SINK_BASIN_DEPTH_RATIO = 0.14;
/** Thinner countertop slab on drainboard sinks so grate ribs read above the deck. */
const DRAINBOARD_DECK_HEIGHT_RATIO = 0.02;
/** Shelf height under the drainboard deck; keeps support without a tall solid block. */
const DRAINBOARD_SUPPORT_HEIGHT_RATIO = 0.035;
/** Thin porcelain floor slab; kept below MIN_SIZE so walls keep most of the depth. */
const PORCELAIN_SINK_BASIN_FLOOR_THICKNESS = 0.012;
const SINK_DETAIL_RENDER_ORDER = 10;
const SINK_SURFACE_DEPTH_BIAS = 2;
const SINK_DETAIL_DEPTH_BIAS = -12;

function flatPorcelainFloorExtrusion(
  id: string,
  points: [number, number][],
  z: number,
  height: number,
  holes?: [number, number][][],
  preserveHeight = false,
): LivingRoom3DPart {
  return {
    id,
    type: "extrudedShape",
    center: [0, 0, z],
    points,
    holes,
    z,
    height: preserveHeight ? height : Math.max(height, MIN_SIZE),
    materialSlot: "doorPanel",
  };
}

function flatPorcelainExtrusion(
  id: string,
  points: [number, number][],
  z: number,
  height: number,
  holes?: [number, number][][],
): LivingRoom3DPart {
  return {
    id,
    type: "extrudedShape",
    center: [0, 0, z],
    points,
    holes,
    z,
    height: Math.max(height, MIN_SIZE),
    materialSlot: "doorPanel",
    bevelSize: 0,
    bevelThickness: 0,
    polygonOffset: true,
    polygonOffsetFactor: SINK_SURFACE_DEPTH_BIAS,
  };
}

function withSinkDetailBias<T extends LivingRoom3DPart>(part: T): T {
  return {
    ...part,
    renderOrder: part.renderOrder ?? SINK_DETAIL_RENDER_ORDER,
    polygonOffset: true,
    polygonOffsetFactor: SINK_DETAIL_DEPTH_BIAS,
    depthWrite: false,
  };
}

/** Keep drainboard grate ribs above the deck surface once depth testing is enabled. */
function withDrainboardGrateOnTop<T extends LivingRoom3DPart>(part: T): T {
  return {
    ...part,
    renderOrder: SINK_DETAIL_RENDER_ORDER + 1,
    polygonOffset: true,
    polygonOffsetFactor: SINK_DETAIL_DEPTH_BIAS,
  };
}

function porcelainSinkPartId(
  base: string,
  bowlId: string,
  singleBowl: boolean,
): string {
  return singleBowl ? base : `${base}-${bowlId}`;
}

type PorcelainBowlPartSet = {
  interior: LivingRoom3DPart[];
  details: LivingRoom3DPart[];
};

/** Flush metal drain disc on the basin floor, matching the bathtub pattern. */
function createPorcelainDrainDetails(
  bowlId: string,
  singleBowl: boolean,
  centerX: number,
  centerY: number,
  floorTopZ: number,
  bowlWidth: number,
  bowlDepth: number,
): LivingRoom3DPart[] {
  const drainRadius = Math.min(bowlWidth, bowlDepth) * 0.038;
  const drainHeight = SINK_DRAIN_HEIGHT;
  const drainZ = floorTopZ + drainHeight / 2;

  return [
    {
      id: porcelainSinkPartId("drain", bowlId, singleBowl),
      type: "cylinder",
      center: [centerX, centerY, drainZ],
      height: drainHeight,
      radiusX: drainRadius,
      radiusY: drainRadius,
      radialSegments: 24,
      materialSlot: "furnitureMetal",
    },
  ];
}

function createPorcelainBowlParts(
  bowlId: string,
  centerX: number,
  centerY: number,
  bowlWidth: number,
  bowlDepth: number,
  singleBowl: boolean,
  wallZ: number,
  wallHeight: number,
  basinFloorZ: number,
  basinFloorHeight: number,
): PorcelainBowlPartSet {
  const { opening, inner } = createPorcelainBowlOutlines(
    centerX,
    centerY,
    bowlWidth,
    bowlDepth,
  );
  const floorTopZ = basinFloorZ + basinFloorHeight;

  return {
    interior: [
      flatPorcelainExtrusion(
        porcelainSinkPartId("sink-basin-wall", bowlId, singleBowl),
        opening,
        wallZ,
        wallHeight,
        [inner],
      ),
      flatPorcelainFloorExtrusion(
        porcelainSinkPartId("sink-basin-floor", bowlId, singleBowl),
        inner,
        basinFloorZ,
        basinFloorHeight,
        undefined,
        true,
      ),
    ],
    details: createPorcelainDrainDetails(
      bowlId,
      singleBowl,
      centerX,
      centerY,
      floorTopZ,
      bowlWidth,
      bowlDepth,
    ),
  };
}

/** Bounds and slot layout for the Sink4 drainboard grate. */
function getDrainboardGrooveLayout(width: number, depth: number) {
  const minX = -width / 2 + width * 0.05;
  const maxX = -width * 0.02;
  const regionWidth = maxX - minX;
  const bowlDepth = depth * PORCELAIN_SINK_BOWL_DEPTH_RATIO;
  const endInset = bowlDepth * 0.12;
  const grooveLength = bowlDepth - endInset * 2;
  const grooveCount = 12;
  const grooveSpacing = regionWidth / (grooveCount + 1);
  const grooveWidth = Math.max(regionWidth * 0.03, 0.0035);
  return {
    grooveCount,
    minX,
    maxX,
    grooveSpacing,
    grooveWidth,
    grooveLength,
    bowlDepth,
    regionWidth,
  };
}

const DRAINBOARD_GROOVE_HEIGHT = 0.004;
const DRAINBOARD_GROOVE_SURFACE_LIFT = 0.005;

/** Raised grate ribs on the drainboard deck surface (Sink4). */
function createDrainboardGrooveParts(
  width: number,
  depth: number,
  deckZ: number,
  deckHeight: number,
): LivingRoom3DPart[] {
  const layout = getDrainboardGrooveLayout(width, depth);
  const deckTop = deckZ + deckHeight;
  const grooveZ =
    deckTop + DRAINBOARD_GROOVE_SURFACE_LIFT + DRAINBOARD_GROOVE_HEIGHT / 2;

  return Array.from({ length: layout.grooveCount }, (_, index) =>
    withDrainboardGrateOnTop(
      box(
        `drainboard-groove-${index}`,
        [layout.minX + layout.grooveSpacing * (index + 1), 0, grooveZ],
        [layout.grooveWidth, layout.grooveLength, DRAINBOARD_GROOVE_HEIGHT],
        "doorHardware",
      ),
    ),
  );
}

type SinkCabinetDrainboardSupport = {
  minX: number;
  maxX: number;
};

/**
 * Full-height cabinet shell with a hollow interior so the bowl can stay deep
 * without a solid counter block filling the cavity.
 */
function createSinkCabinetFrameParts(
  width: number,
  depth: number,
  bodyBottomZ: number,
  bodyHeight: number,
  materialSlot: FloorPlan3DMaterialSlot = "doorPanel",
  drainboardSupport?: SinkCabinetDrainboardSupport,
  drainboardSupportHeight?: number,
): LivingRoom3DPart[] {
  const panelThickness = clamp(Math.min(width, depth) * 0.045, 0.018, 0.03);
  const innerWidth = Math.max(width - panelThickness * 2, MIN_SIZE);
  const innerDepth = Math.max(depth - panelThickness * 2, MIN_SIZE);
  const centerZ = bodyBottomZ + bodyHeight / 2;
  const frameMinSize = 0.001;

  const parts: LivingRoom3DPart[] = [
    box(
      "counter-left",
      [-width / 2 + panelThickness / 2, 0, centerZ],
      [panelThickness, depth, bodyHeight],
      materialSlot,
      false,
      undefined,
      frameMinSize,
    ),
    box(
      "counter-right",
      [width / 2 - panelThickness / 2, 0, centerZ],
      [panelThickness, depth, bodyHeight],
      materialSlot,
      false,
      undefined,
      frameMinSize,
    ),
    box(
      "counter",
      [0, -depth / 2 + panelThickness / 2, centerZ],
      [width, panelThickness, bodyHeight],
      materialSlot,
      false,
      undefined,
      frameMinSize,
    ),
    box(
      "counter-back",
      [0, depth / 2 - panelThickness / 2, centerZ],
      [innerWidth, panelThickness, bodyHeight],
      materialSlot,
      false,
      undefined,
      frameMinSize,
    ),
  ];

  if (drainboardSupport) {
    const supportWidth = drainboardSupport.maxX - drainboardSupport.minX;
    const supportCenterX =
      (drainboardSupport.minX + drainboardSupport.maxX) / 2;
    const supportHeight = Math.min(
      drainboardSupportHeight ?? bodyHeight,
      bodyHeight,
    );
    const supportCenterZ = bodyBottomZ + bodyHeight - supportHeight / 2;
    parts.push(
      box(
        "counter-drainboard-support",
        [supportCenterX, 0, supportCenterZ],
        [supportWidth, innerDepth, supportHeight],
        materialSlot,
        false,
        undefined,
        frameMinSize,
      ),
    );
  }

  return parts;
}

/** Recessed porcelain sink with deck cutouts, shared by single/double/drainboard models. */
function createPorcelainSinkParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  toe: KitchenToeMetrics,
): LivingRoom3DPart[] {
  const bowlSpecs = getPorcelainSinkBowlSpecs(modelKey, width);
  const singleBowl = bowlSpecs.length === 1;
  const isDrainboardSink = modelKey === "kitchen:sink-drainboard";
  const deckHeight = isDrainboardSink
    ? Math.max(height * DRAINBOARD_DECK_HEIGHT_RATIO, 0.012)
    : Math.max(height * 0.045, MIN_SIZE);
  const drainboardSupportHeight = isDrainboardSink
    ? Math.max(height * DRAINBOARD_SUPPORT_HEIGHT_RATIO, deckHeight * 1.5)
    : undefined;
  const bodyHeight = Math.max(toe.bodyHeight - deckHeight, MIN_SIZE);
  const deckZ = toe.bodyBottomZ + bodyHeight;
  const deckTop = deckZ + deckHeight;
  const deckOutline = createRoundedRectOutline(
    0,
    0,
    width,
    depth,
    Math.min(depth * 0.035, width * 0.025),
    6,
  );
  const basinDepth = Math.min(
    Math.max(height * PORCELAIN_SINK_BASIN_DEPTH_RATIO, MIN_SIZE),
    Math.max(bodyHeight - deckHeight, MIN_SIZE),
  );
  const basinFloorHeight = PORCELAIN_SINK_BASIN_FLOOR_THICKNESS;
  const basinFloorZ = deckZ - basinDepth;
  const wallHeight = basinDepth - basinFloorHeight;
  const wallZ = basinFloorZ + basinFloorHeight;

  const bowlLayouts = bowlSpecs.map((spec) => {
    const centerY = spec.centerY ?? 0;
    const bowlWidth = width * spec.widthRatio;
    const bowlDepth = depth * spec.depthRatio;
    return {
      ...spec,
      centerY,
      bowlWidth,
      bowlDepth,
      outlines: createPorcelainBowlOutlines(
        spec.centerX,
        centerY,
        bowlWidth,
        bowlDepth,
      ),
    };
  });

  const bowlPartSets = bowlLayouts.map((bowl) =>
    createPorcelainBowlParts(
      bowl.id,
      bowl.centerX,
      bowl.centerY,
      bowl.bowlWidth,
      bowl.bowlDepth,
      singleBowl,
      wallZ,
      wallHeight,
      basinFloorZ,
      basinFloorHeight,
    ),
  );
  const drainboardLayout =
    modelKey === "kitchen:sink-drainboard"
      ? getDrainboardGrooveLayout(width, depth)
      : undefined;

  return [
    ...createSinkCabinetFrameParts(
      width,
      depth,
      toe.bodyBottomZ,
      bodyHeight,
      "doorPanel",
      drainboardLayout
        ? { minX: drainboardLayout.minX, maxX: drainboardLayout.maxX }
        : undefined,
      drainboardSupportHeight,
    ),
    ...createKitchenToeSupportParts(width, toe, "doorPanel"),
    (isDrainboardSink
      ? (
          id: string,
          points: [number, number][],
          z: number,
          deckPartHeight: number,
          holes?: [number, number][][],
        ) =>
          flatPorcelainFloorExtrusion(
            id,
            points,
            z,
            deckPartHeight,
            holes,
            true,
          )
      : flatPorcelainExtrusion)(
      "sink-deck",
      deckOutline,
      deckZ,
      deckHeight,
      bowlLayouts.map((bowl) => bowl.outlines.opening),
    ),
    ...bowlPartSets.flatMap((parts) => parts.interior),
    ...createFaucetParts(
      width,
      depth,
      height,
      deckTop,
      PORCELAIN_SINK_FAUCET_OPTIONS,
    ),
    ...bowlPartSets.flatMap((parts) => parts.details),
    ...(modelKey === "kitchen:sink-drainboard" && drainboardLayout
      ? createDrainboardGrooveParts(width, depth, deckZ, deckHeight)
      : []),
  ];
}

function createKitchenParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const toe = getKitchenToeMetrics(width, depth, height, baseElevation);

  if (modelKey === "kitchen:cabinet") {
    return createCabinetParts(width, depth, height, toe);
  }

  if (modelKey === "kitchen:dishwasher") {
    return createDishwasherParts(width, depth, toe);
  }

  if (modelKey.includes("fridge")) {
    const doorGap = Math.max(depth * 0.006, 0.003);
    const doorThickness = Math.max(depth * 0.018, 0.01);
    const doorCenterY = -depth / 2 - doorGap - doorThickness / 2;
    const doorVerticalGap = Math.max(toe.toeHeight * 0.18, 0.02);
    const lowerDoorHeight = toe.bodyHeight * 0.27;
    const lowerDoorBottomZ = toe.bodyBottomZ + doorVerticalGap;
    const lowerDoorTopZ = lowerDoorBottomZ + lowerDoorHeight;
    const upperDoorBottomZ = lowerDoorTopZ + doorVerticalGap;
    const upperDoorTopZ = baseElevation + height - doorVerticalGap;
    const upperDoorHeight = Math.max(
      upperDoorTopZ - upperDoorBottomZ,
      MIN_SIZE,
    );
    return [
      box(
        "fridge-body",
        [0, 0, toe.bodyBottomZ + toe.bodyHeight / 2],
        [width, depth, toe.bodyHeight],
        "furnitureMetal",
      ),
      ...createKitchenToeSupportParts(width, toe),
      box(
        "door-upper",
        [0, doorCenterY, upperDoorBottomZ + upperDoorHeight / 2],
        [width * 0.9, doorThickness, upperDoorHeight],
        "furnitureGlass",
        false,
        undefined,
        0.004,
      ),
      box(
        "door-lower",
        [0, doorCenterY, lowerDoorBottomZ + lowerDoorHeight / 2],
        [width * 0.9, doorThickness, lowerDoorHeight],
        "furnitureGlass",
        false,
        undefined,
        0.004,
      ),
    ];
  }
  if (modelKey.includes("sink")) {
    return createPorcelainSinkParts(modelKey, width, depth, height, toe);
  }
  if (modelKey.includes("stove")) {
    /**
     * Align cooktop top with Property Panel height. Body is shortened by
     * cooktop thickness so the surface (not the body underside) sits at
     * `baseElevation + height`; burners may protrude slightly above.
     * Thickness is floored at MIN_SIZE so box() does not silently thicken it.
     */
    const cooktopThickness = clamp(height * 0.04, MIN_SIZE, 0.05);
    const bodyHeight = Math.max(toe.bodyHeight - cooktopThickness, MIN_SIZE);
    const cooktopTop = baseElevation + height;
    const cooktopCenterZ = cooktopTop - cooktopThickness / 2;
    const burnerHeight = Math.max(0.025, MIN_SIZE);
    const burnerCenterZ = cooktopTop + burnerHeight / 2;
    return [
      box(
        "stove-body",
        [0, 0, toe.bodyBottomZ + bodyHeight / 2],
        [width, depth, bodyHeight],
        "furnitureMetal",
      ),
      ...createKitchenToeSupportParts(width, toe),
      box(
        "cooktop",
        [0, 0, cooktopCenterZ],
        [width, depth, cooktopThickness],
        "furnitureScreen",
      ),
      ...createBurners(width, depth, burnerCenterZ),
    ];
  }
  return createCabinetParts(width, depth, height, toe);
}

type KitchenToeMetrics = {
  toeHeight: number;
  bodyBottomZ: number;
  bodyHeight: number;
  toeCenterY: number;
  toeDepth: number;
  supportLegSize: number;
  supportLegZ: number;
  supportLegY: number;
  supportLegX: number;
};

function getKitchenToeMetrics(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): KitchenToeMetrics {
  const toeHeight = Math.min(
    KITCHEN_TOE_HEIGHT,
    Math.max(height * 0.25, MIN_SIZE),
  );
  const bodyFrontY = -depth / 2;
  const toeDepth = Math.max(depth * 0.13, MIN_SIZE);
  const toeFrontRecess = Math.max(depth * 0.08, 0.04);
  const supportLegSize = clamp(Math.min(width, depth) * 0.08, 0.04, 0.055);

  return {
    toeHeight,
    bodyBottomZ: baseElevation + toeHeight,
    bodyHeight: Math.max(height - toeHeight, MIN_SIZE),
    toeCenterY: bodyFrontY + toeFrontRecess + toeDepth / 2,
    toeDepth,
    supportLegSize,
    supportLegZ: baseElevation + toeHeight / 2,
    supportLegY: depth / 2 - supportLegSize / 2,
    supportLegX: width / 2 - supportLegSize / 2,
  };
}

function createKitchenToeSupportParts(
  width: number,
  toe: KitchenToeMetrics,
  materialSlot: FloorPlan3DMaterialSlot = "furnitureMetal",
): LivingRoom3DPart[] {
  return [
    box(
      "toe-kick",
      [0, toe.toeCenterY, toe.supportLegZ],
      [width, toe.toeDepth, toe.toeHeight],
      materialSlot,
    ),
    box(
      "rear-left-leg",
      [-toe.supportLegX, toe.supportLegY, toe.supportLegZ],
      [toe.supportLegSize, toe.supportLegSize, toe.toeHeight],
      materialSlot,
    ),
    box(
      "rear-right-leg",
      [toe.supportLegX, toe.supportLegY, toe.supportLegZ],
      [toe.supportLegSize, toe.supportLegSize, toe.toeHeight],
      materialSlot,
    ),
  ];
}

function createCabinetParts(
  width: number,
  depth: number,
  height: number,
  toe: KitchenToeMetrics,
): LivingRoom3DPart[] {
  const topThickness = clamp(height * 0.06, 0.04, 0.06);
  const bodyHeight = Math.max(toe.bodyHeight - topThickness, MIN_SIZE);
  const bodyBottomZ = toe.bodyBottomZ;
  const bodyCenterZ = bodyBottomZ + bodyHeight / 2;
  const topCenterZ = bodyBottomZ + bodyHeight + topThickness / 2;
  const bodyFrontY = -depth / 2;
  const doorGap = Math.max(depth * 0.006, 0.003);
  const doorThickness = Math.max(depth * 0.018, 0.01);
  const doorHeight = bodyHeight * 0.82;
  const doorCenterZ = bodyBottomZ + bodyHeight * 0.48;
  const doorCenterY = bodyFrontY - doorGap - doorThickness / 2;
  const handleDepth = Math.max(depth * 0.035, 0.018);
  const handleWidth = Math.max(width * 0.025, 0.012);
  const handleHeight = bodyHeight * 0.38;
  const handleCenterY =
    doorCenterY - doorThickness / 2 - doorGap - handleDepth / 2;

  return [
    box(
      "cabinet-body",
      [0, 0, bodyCenterZ],
      [width, depth, bodyHeight],
      "furnitureWood",
    ),
    box(
      "cabinet-top",
      [0, 0, topCenterZ],
      [width * 1.02, depth * 1.02, topThickness],
      "doorPanel",
    ),
    ...createKitchenToeSupportParts(width, toe, "furnitureWood"),
    box(
      "door-panel",
      [0, doorCenterY, doorCenterZ],
      [width * 0.78, doorThickness, doorHeight],
      "furnitureMetal",
      false,
      undefined,
      0.004,
    ),
    box(
      "door-handle",
      [width * 0.28, handleCenterY, doorCenterZ],
      [handleWidth, handleDepth, handleHeight],
      "doorHardware",
      false,
      undefined,
      0.004,
    ),
  ];
}

/**
 * Under-counter dishwasher: metal body, full-face door, control strip, and
 * horizontal bar handle, sharing the kitchen toe plinth with other units.
 */
function createDishwasherParts(
  width: number,
  depth: number,
  toe: KitchenToeMetrics,
): LivingRoom3DPart[] {
  const bodyBottomZ = toe.bodyBottomZ;
  const bodyHeight = toe.bodyHeight;
  const bodyCenterZ = bodyBottomZ + bodyHeight / 2;
  const bodyFrontY = -depth / 2;
  const doorGap = Math.max(depth * 0.006, 0.003);
  const doorThickness = Math.max(depth * 0.02, 0.012);
  const doorInset = Math.max(width * 0.02, 0.008);
  const doorWidth = Math.max(width - doorInset * 2, MIN_SIZE);
  const doorHeight = Math.max(bodyHeight * 0.92, MIN_SIZE);
  const doorCenterZ = bodyBottomZ + bodyHeight * 0.5;
  const doorCenterY = bodyFrontY - doorGap - doorThickness / 2;
  const doorFrontY = doorCenterY - doorThickness / 2;

  const controlHeight = clamp(bodyHeight * 0.07, 0.03, 0.05);
  const controlThickness = Math.max(doorThickness * 0.45, 0.004);
  const controlCenterZ =
    doorCenterZ + doorHeight / 2 - controlHeight / 2 - doorHeight * 0.02;
  const controlCenterY = doorFrontY - doorGap - controlThickness / 2;

  const handleHeight = clamp(bodyHeight * 0.03, 0.012, 0.02);
  const handleDepth = Math.max(depth * 0.03, 0.016);
  const handleWidth = Math.max(doorWidth * 0.72, MIN_SIZE);
  const handleCenterZ = controlCenterZ - controlHeight / 2 - handleHeight * 1.6;
  const handleCenterY = doorFrontY - doorGap - handleDepth / 2;

  return [
    box(
      "dishwasher-body",
      [0, 0, bodyCenterZ],
      [width, depth, bodyHeight],
      "furnitureMetal",
    ),
    ...createKitchenToeSupportParts(width, toe),
    box(
      "door-panel",
      [0, doorCenterY, doorCenterZ],
      [doorWidth, doorThickness, doorHeight],
      "doorPanel",
      false,
      undefined,
      0.004,
    ),
    box(
      "control-panel",
      [0, controlCenterY, controlCenterZ],
      [doorWidth * 0.92, controlThickness, controlHeight],
      "furnitureScreen",
      false,
      undefined,
      0.003,
    ),
    box(
      "door-handle",
      [0, handleCenterY, handleCenterZ],
      [handleWidth, handleDepth, handleHeight],
      "doorHardware",
      false,
      undefined,
      0.003,
    ),
  ];
}

function createDiningParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const tableSurfaceHeight = getDiningTableSurfaceHeight(height);
  const tabletopThickness = getDiningTabletopThickness(height);

  if (
    modelKey === "dining:round-table" ||
    modelKey === "dining:round-table-plain"
  ) {
    return createRoundDiningTableParts(
      width,
      depth,
      baseElevation,
      tableSurfaceHeight,
      tabletopThickness,
      modelKey === "dining:round-table",
    );
  }

  if (modelKey === "dining:table-plain") {
    return createRectangularDiningTableParts(
      4,
      width,
      depth,
      baseElevation,
      tableSurfaceHeight,
      tabletopThickness,
      false,
    );
  }

  const chairCount =
    modelKey === "dining:table-6-chair"
      ? 6
      : modelKey === "dining:table-4-chair"
        ? 4
        : 2;
  return createRectangularDiningTableParts(
    chairCount,
    width,
    depth,
    baseElevation,
    tableSurfaceHeight,
    tabletopThickness,
  );
}

function createStandaloneDiningChairParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  return createDiningChairParts(
    "standalone",
    0,
    0,
    // The standalone chair is rotated -90° to follow its 2D symbol. Its local
    // width therefore maps to world depth, and local depth maps to world width.
    depth,
    width,
    baseElevation,
    clamp(height * 0.93, 0.72, DINING_TABLE_SURFACE_HEIGHT),
    0,
  );
}

/**
 * Open bookshelf shell: optional left/right frame, optional back panel, and N
 * boards (including top and bottom) spaced by the configured clear opening.
 */
function createBookShelfParts(symbol: BookShelfType): LivingRoom3DPart[] {
  const layout = resolveBookShelfLayout(symbol.bookShelf);
  const baseElevation = symbol.threeD?.baseElevation ?? 0;
  const { width, depth, height, hasFrame } = layout.config;
  const {
    sideThickness,
    boardThickness,
    backThickness,
    innerWidth,
    shelfDepth,
    shelfCenterY,
    shelves,
  } = layout;
  const sideCenterZ = baseElevation + height / 2;
  const parts: LivingRoom3DPart[] = [];

  if (hasFrame && sideThickness > 0) {
    parts.push(
      box(
        "bookshelf-side-left",
        [-width / 2 + sideThickness / 2, 0, sideCenterZ],
        [sideThickness, depth, height],
        "furnitureWood",
      ),
      box(
        "bookshelf-side-right",
        [width / 2 - sideThickness / 2, 0, sideCenterZ],
        [sideThickness, depth, height],
        "furnitureWood",
      ),
    );
  }

  if (layout.config.hasBackPanel && backThickness > 0) {
    parts.push(
      box(
        "bookshelf-back",
        [0, depth / 2 - backThickness / 2, sideCenterZ],
        [innerWidth, backThickness, height],
        "furnitureWood",
      ),
    );
  }

  shelves.forEach((shelf) => {
    parts.push(
      box(
        shelf.id,
        [0, shelfCenterY, baseElevation + shelf.centerZ],
        [innerWidth, shelfDepth, boardThickness],
        "furnitureWood",
      ),
    );
  });

  return parts;
}

function createSmartTableParts(symbol: SmartTableType): LivingRoom3DPart[] {
  const layout = resolveSmartTableLayout(symbol.smartTable);
  const baseElevation = symbol.threeD?.baseElevation ?? 0;
  const tableHeight = normalizeSmartTableHeight(symbol.threeD?.height);
  const supportHeight = tableHeight - SMART_TABLE_TOP_THICKNESS_M;
  const supportZ = baseElevation + supportHeight / 2;
  const tabletopZ =
    baseElevation + tableHeight - SMART_TABLE_TOP_THICKNESS_M / 2;
  const parts: LivingRoom3DPart[] = [];

  if (layout.tabletop.shape === "round") {
    parts.push(
      cylinder(
        "smart-tabletop",
        [0, 0, tabletopZ],
        SMART_TABLE_TOP_THICKNESS_M,
        layout.tabletop.width / 2,
        layout.tabletop.length / 2,
        64,
        "furnitureWood",
        true,
      ),
    );
  } else {
    parts.push(
      box(
        "smart-tabletop",
        [0, 0, tabletopZ],
        [
          layout.tabletop.width,
          layout.tabletop.length,
          SMART_TABLE_TOP_THICKNESS_M,
        ],
        "furnitureWood",
        true,
      ),
    );
  }

  layout.supports.forEach((support) => {
    if (support.kind === "leg") {
      parts.push(
        box(
          support.id,
          [support.x, support.y, supportZ],
          [support.size, support.size, supportHeight],
          "furnitureMetal",
          false,
          undefined,
          support.size,
        ),
      );
      return;
    }

    const baseThickness = 0.04;
    const columnHeight = Math.max(
      supportHeight - baseThickness,
      MIN_SIZE,
    );
    parts.push(
      cylinder(
        `${support.id}-base`,
        [support.x, support.y, baseElevation + baseThickness / 2],
        baseThickness,
        support.baseDiameter / 2,
        support.baseDiameter / 2,
        48,
        "furnitureMetal",
      ),
      cylinder(
        `${support.id}-column`,
        [
          support.x,
          support.y,
          baseElevation + baseThickness + columnHeight / 2,
        ],
        columnHeight,
        support.columnDiameter / 2,
        support.columnDiameter / 2,
        32,
        "furnitureMetal",
      ),
    );
  });

  layout.chairs.forEach((chair) => {
    if (chair.model === "armchair") {
      parts.push(...createPlacedDiningArmchairParts(chair, baseElevation));
      return;
    }
    parts.push(
      ...createDiningChairParts(
        chair.id,
        chair.x,
        chair.y,
        chair.width,
        chair.depth,
        baseElevation,
        DINING_TABLE_SURFACE_HEIGHT,
        chair.outwardAngle,
      ),
    );
  });

  return parts;
}

function createPlacedDiningArmchairParts(
  chair: ReturnType<typeof resolveSmartTableLayout>["chairs"][number],
  baseElevation: number,
): LivingRoom3DPart[] {
  const rotationZ = chair.outwardAngle - Math.PI / 2;
  return createDiningArmchairParts(
    chair.width,
    chair.depth,
    chair.height,
    baseElevation,
    true,
  ).map((part) => {
    if (part.type === "box") {
      const [offsetX, offsetY] = rotateOffset(
        part.center[0],
        part.center[1],
        rotationZ,
      );
      return {
        ...part,
        id: `chair-${chair.id}-${part.id}`,
        center: [
          chair.x + offsetX,
          chair.y + offsetY,
          part.center[2],
        ],
        rotationZ: (part.rotationZ ?? 0) + rotationZ,
      };
    }
    if (part.type === "extrudedShape") {
      const placePoint = ([x, y]: [number, number]): [number, number] => {
        const [offsetX, offsetY] = rotateOffset(x, y, rotationZ);
        return [chair.x + offsetX, chair.y + offsetY];
      };
      return {
        ...part,
        id: `chair-${chair.id}-${part.id}`,
        center: [0, 0, part.center[2]],
        points: part.points.map(placePoint),
        holes: part.holes?.map((hole) => hole.map(placePoint)),
      };
    }
    return part;
  });
}

function createDiningArmchairParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
  fitSmartTableFootprint = false,
): LivingRoom3DPart[] {
  const smartTableProfile = fitSmartTableFootprint
    ? resolveSmartTableArmchairProfile(width, depth)
    : undefined;
  const tableSurfaceHeight = clamp(height, 0.72, DINING_TABLE_SURFACE_HEIGHT);
  const seatSurfaceHeight = clamp(
    tableSurfaceHeight - 0.3,
    0.43,
    DINING_SEAT_SURFACE_HEIGHT,
  );
  const seatThickness = DINING_SEAT_THICKNESS;
  const seatBottomZ = baseElevation + seatSurfaceHeight - seatThickness;
  const legHeight = Math.max(seatBottomZ - baseElevation, MIN_SIZE);
  const legZ = baseElevation + legHeight / 2;
  const legSize = clamp(
    Math.min(width, depth) * 0.07,
    DINING_CHAIR_LEG_SIZE,
    0.034,
  );
  const armWidth =
    smartTableProfile?.arm.width ?? clamp(width * 0.075, 0.032, 0.045);
  const armDepth = smartTableProfile?.arm.depth ?? depth * 0.66;
  const armHeight = clamp(height * 0.07, 0.045, 0.06);
  const armSurfaceHeight = clamp(seatSurfaceHeight + 0.18, 0.62, 0.66);
  const armBottomZ = baseElevation + armSurfaceHeight - armHeight;
  const backBottomZ = seatBottomZ;
  const backDepth =
    smartTableProfile?.back.depth ?? clamp(depth * 0.16, 0.06, 0.08);
  const backHeight = clamp(baseElevation + height - backBottomZ, 0.28, 0.36);
  const seatWidth = smartTableProfile?.seat.width ?? width * 0.92;
  const seatDepth = smartTableProfile?.seat.depth ?? depth * 0.74;
  const seatY = smartTableProfile?.seat.centerY ?? -depth * 0.04;
  const seatRearY = seatY + seatDepth / 2;
  const backY =
    smartTableProfile?.back.centerY ??
    Math.min(
      depth / 2 - backDepth / 2,
      seatRearY + backDepth * 0.2,
    );
  const armY = smartTableProfile?.arm.centerY ?? -depth * 0.04;
  const armX =
    smartTableProfile?.arm.centerX ?? seatWidth / 2 - armWidth / 2;
  const railZ = baseElevation + legHeight * 0.72;
  const frontArmSupportHeight = Math.max(
    armBottomZ - (baseElevation + seatSurfaceHeight),
    MIN_SIZE,
  );
  const frontArmSupportZ =
    baseElevation + seatSurfaceHeight + frontArmSupportHeight / 2;
  const legOffsets = [
    [-width * 0.34, -depth * 0.3],
    [width * 0.34, -depth * 0.3],
    [-width * 0.34, depth * 0.25],
    [width * 0.34, depth * 0.25],
  ] as const;
  const legNames = ["front-left", "front-right", "back-left", "back-right"];

  return [
    ...legOffsets.map(([x, y], index) =>
      box(
        `dining-armchair-leg-${legNames[index]}`,
        [x, y, legZ],
        [legSize, legSize, legHeight],
        "furnitureMetal",
        false,
        undefined,
        legSize,
      ),
    ),
    box(
      "dining-armchair-front-rail",
      [0, -depth * 0.31, railZ],
      [width * 0.68, DINING_CHAIR_RAIL_SIZE, DINING_CHAIR_RAIL_SIZE],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
    box(
      "dining-armchair-back-rail",
      [0, depth * 0.25, railZ],
      [width * 0.68, DINING_CHAIR_RAIL_SIZE, DINING_CHAIR_RAIL_SIZE],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
    box(
      "dining-armchair-left-rail",
      [-width * 0.34, -depth * 0.03, railZ],
      [DINING_CHAIR_RAIL_SIZE, depth * 0.56, DINING_CHAIR_RAIL_SIZE],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
    box(
      "dining-armchair-right-rail",
      [width * 0.34, -depth * 0.03, railZ],
      [DINING_CHAIR_RAIL_SIZE, depth * 0.56, DINING_CHAIR_RAIL_SIZE],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
    roundedDiningChairPart(
      "dining-armchair-seat-cushion",
      0,
      seatY,
      seatBottomZ,
      seatWidth,
      seatDepth,
      seatThickness,
      Math.min(seatWidth, seatDepth) * 0.18,
    ),
    roundedDiningChairPart(
      "dining-armchair-backrest",
      0,
      backY,
      backBottomZ,
      smartTableProfile?.back.width ?? width * 0.88,
      backDepth,
      backHeight,
      backDepth * 0.48,
    ),
    roundedDiningChairPart(
      "dining-armchair-left-arm",
      -armX,
      armY,
      armBottomZ,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.48,
    ),
    box(
      "dining-armchair-left-arm-front-support",
      [-armX, armY - armDepth * 0.42, frontArmSupportZ],
      [armWidth * 0.72, DINING_CHAIR_RAIL_SIZE, frontArmSupportHeight],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
    roundedDiningChairPart(
      "dining-armchair-right-arm",
      armX,
      armY,
      armBottomZ,
      armWidth,
      armDepth,
      armHeight,
      armWidth * 0.48,
    ),
    box(
      "dining-armchair-right-arm-front-support",
      [armX, armY - armDepth * 0.42, frontArmSupportZ],
      [armWidth * 0.72, DINING_CHAIR_RAIL_SIZE, frontArmSupportHeight],
      "furnitureMetal",
      false,
      undefined,
      DINING_CHAIR_RAIL_SIZE,
    ),
  ];
}

function createRoundDiningTableParts(
  width: number,
  depth: number,
  baseElevation: number,
  tableSurfaceHeight: number,
  tabletopThickness: number,
  includeChairs = true,
): LivingRoom3DPart[] {
  const scales = includeChairs
    ? diningRoundFootprintScales(width, depth)
    : {
        scaleX: width / DINING_ROUND_TABLE_DIAMETER_M,
        scaleY: depth / DINING_ROUND_TABLE_DIAMETER_M,
      };
  const tabletopZ = baseElevation + tableSurfaceHeight - tabletopThickness / 2;
  const tabletopRadiusX = diningRoundTableRadiusM() * scales.scaleX;
  const tabletopRadiusY = diningRoundTableRadiusM() * scales.scaleY;
  const apronHeight = clamp(tabletopThickness * 0.8, 0.038, 0.052);
  const apronZ =
    baseElevation + tableSurfaceHeight - tabletopThickness - apronHeight / 2;
  const legHeight = Math.max(
    tableSurfaceHeight - tabletopThickness - apronHeight,
    MIN_SIZE,
  );
  const legRadius = Math.max(Math.min(width, depth) * 0.012, 0.018);
  const legRadiusX = tabletopRadiusX * 0.6;
  const legRadiusY = tabletopRadiusY * 0.6;

  return [
    cylinder(
      "tabletop",
      [0, 0, tabletopZ],
      tabletopThickness,
      tabletopRadiusX,
      tabletopRadiusY,
      48,
      "furnitureWood",
      true,
    ),
    cylinder(
      "table-apron",
      [0, 0, apronZ],
      apronHeight,
      tabletopRadiusX * 0.98,
      tabletopRadiusY * 0.98,
      48,
      "furnitureWood",
      true,
    ),
    ...Array.from({ length: 4 }, (_, index) => {
      const angle = Math.PI / 4 + (index * Math.PI) / 2;
      return cylinder(
        `round-table-leg-${index}`,
        [
          Math.cos(angle) * legRadiusX,
          Math.sin(angle) * legRadiusY,
          baseElevation + legHeight / 2,
        ],
        legHeight,
        legRadius,
        legRadius,
        12,
        "furnitureMetal",
      );
    }),
    ...(includeChairs
      ? createDiningChairsAroundCircle(
          scales,
          baseElevation,
          DINING_TABLE_SURFACE_HEIGHT,
        )
      : []),
  ];
}

function createRectangularDiningTableParts(
  chairCount: number,
  width: number,
  depth: number,
  baseElevation: number,
  tableSurfaceHeight: number,
  tabletopThickness: number,
  includeChairs = true,
): LivingRoom3DPart[] {
  const layout = getDiningRectLayoutForChairCount(chairCount);
  const scales = includeChairs
    ? diningRectFootprintScales(chairCount, width, depth)
    : {
        scaleX: width / layout.tableWidth,
        scaleY: depth / layout.tableDepth,
      };
  const tabletopWidth = layout.tableWidth * scales.scaleX;
  const tabletopDepth = layout.tableDepth * scales.scaleY;
  const apronHeight = clamp(tabletopThickness * 0.75, 0.035, 0.052);
  const tabletopBottomZ =
    baseElevation + tableSurfaceHeight - tabletopThickness;
  const apronZ = tabletopBottomZ - apronHeight / 2;
  const legHeight = Math.max(
    tableSurfaceHeight - tabletopThickness - apronHeight,
    MIN_SIZE,
  );
  const chamfer = Math.min(tabletopWidth, tabletopDepth) * 0.045;

  return [
    extrudedShape(
      "tabletop",
      createChamferedRectOutline(tabletopWidth, tabletopDepth, chamfer),
      tabletopBottomZ,
      tabletopThickness,
      "furnitureWood",
      true,
    ),
    box(
      "table-apron",
      [0, 0, apronZ],
      [tabletopWidth * 0.92, tabletopDepth * 0.92, apronHeight],
      "furnitureWood",
      true,
    ),
    ...createLegs(
      tabletopWidth * 0.72,
      tabletopDepth * 0.82,
      baseElevation,
      legHeight,
      0,
      0,
      "furnitureMetal",
    ),
    ...(includeChairs
      ? createDiningChairs(
          scales.scaleX,
          scales.scaleY,
          layout.tableWidth,
          layout.tableDepth,
          layout.chairRows,
          layout.chairRowSpread,
          baseElevation,
          DINING_TABLE_SURFACE_HEIGHT,
        )
      : []),
  ];
}

function createBurners(
  width: number,
  depth: number,
  z: number,
): LivingRoom3DPart[] {
  const radiusX = width * 0.08;
  const radiusY = depth * 0.08;
  return [
    cylinder(
      "burner-a",
      [-width * 0.22, -depth * 0.18, z],
      0.025,
      radiusX,
      radiusY,
      20,
      "furnitureMetal",
    ),
    cylinder(
      "burner-b",
      [width * 0.2, -depth * 0.18, z],
      0.025,
      radiusX,
      radiusY,
      20,
      "furnitureMetal",
    ),
    cylinder(
      "burner-c",
      [-width * 0.22, depth * 0.18, z],
      0.025,
      radiusX,
      radiusY,
      20,
      "furnitureMetal",
    ),
    cylinder(
      "burner-d",
      [width * 0.2, depth * 0.18, z],
      0.025,
      radiusX,
      radiusY,
      20,
      "furnitureMetal",
    ),
  ];
}

function createDiningChairs(
  scaleX: number,
  scaleY: number,
  tableWidthM: number,
  tableDepthM: number,
  chairRows: number,
  chairRowSpread: number,
  baseElevation: number,
  tableSurfaceHeight: number,
): LivingRoom3DPart[] {
  const parts: LivingRoom3DPart[] = [];
  const chairWidth = DINING_CHAIR_WIDTH * scaleY;
  const chairDepth = DINING_CHAIR_DEPTH * scaleX;
  const xOffset = diningRectChairXOffsetM(tableWidthM) * scaleX;
  const tableDepth = tableDepthM * scaleY;
  const tableTop = -tableDepth / 2;
  const tableBottom = tableDepth / 2;
  const yOffsets = diningChairRowCentersM(
    tableTop,
    tableBottom,
    chairRows,
    chairRowSpread,
  );

  yOffsets.forEach((y, index) => {
    parts.push(
      ...createDiningChairParts(
        `left-${index}`,
        -xOffset,
        y,
        chairWidth,
        chairDepth,
        baseElevation,
        tableSurfaceHeight,
        Math.PI,
      ),
    );
    parts.push(
      ...createDiningChairParts(
        `right-${index}`,
        xOffset,
        y,
        chairWidth,
        chairDepth,
        baseElevation,
        tableSurfaceHeight,
        0,
      ),
    );
  });
  return parts;
}

function createDiningChairsAroundCircle(
  scales: ReturnType<typeof diningRoundFootprintScales>,
  baseElevation: number,
  tableSurfaceHeight: number,
): LivingRoom3DPart[] {
  const parts: LivingRoom3DPart[] = [];

  for (let index = 0; index < 6; index += 1) {
    const angle = (Math.PI * 2 * index) / 6;
    const [x, y] = diningRoundChairPositionAtAngle(angle, scales);
    const { chairWidth, chairDepth } = diningRoundChairScalesAtAngle(
      angle,
      scales,
    );
    parts.push(
      ...createDiningChairParts(
        `round-${index}`,
        x,
        y,
        chairWidth,
        chairDepth,
        baseElevation,
        tableSurfaceHeight,
        angle,
      ),
    );
  }
  return parts;
}

function createDiningChairParts(
  id: string,
  x: number,
  y: number,
  width: number,
  depth: number,
  baseElevation: number,
  tableSurfaceHeight: number,
  outwardAngle = Math.PI / 2,
): LivingRoom3DPart[] {
  const rotationZ = outwardAngle - Math.PI / 2;
  const seatSurfaceHeight = clamp(
    tableSurfaceHeight - 0.3,
    0.43,
    DINING_SEAT_SURFACE_HEIGHT,
  );
  const seatHeight = DINING_SEAT_THICKNESS;
  const seatZ = baseElevation + seatSurfaceHeight - seatHeight / 2;
  const legHeight = Math.max(seatZ - seatHeight / 2 - baseElevation, MIN_SIZE);
  const legZ = baseElevation + legHeight / 2;
  const legSize = clamp(
    Math.min(width, depth) * 0.08,
    DINING_CHAIR_LEG_SIZE,
    0.036,
  );
  const backHeight = clamp(tableSurfaceHeight * 0.52, 0.36, 0.44);
  const backThickness = clamp(depth * 0.1, 0.035, 0.055);
  const crestRearOffset = backThickness * (0.08 + 1.18 / 2);
  const backLocalY = depth / 2 - crestRearOffset;
  const backZ = baseElevation + seatSurfaceHeight + backHeight / 2 - 0.018;
  const postHeight = backHeight * 1.05;
  const postZ = baseElevation + seatSurfaceHeight + postHeight / 2 - 0.026;
  const backPostWidth = legSize * 1.25;
  const backPostX = width / 2 - backPostWidth / 2;
  const crestZ = baseElevation + seatSurfaceHeight + backHeight - 0.024;
  const lowerBackBarZ = baseElevation + seatSurfaceHeight + backHeight * 0.2;
  const legOffsets = [
    [-width * 0.36, -depth * 0.3],
    [width * 0.36, -depth * 0.3],
    [-width * 0.36, depth * 0.3],
    [width * 0.36, depth * 0.3],
  ] as const;
  const legNames = ["front-left", "front-right", "back-left", "back-right"];
  const railZ = baseElevation + legHeight * 0.72;
  const railCenters = [
    ["front-rail", 0, -depth * 0.3, width * 0.72, DINING_CHAIR_RAIL_SIZE],
    ["back-rail", 0, depth * 0.3, width * 0.72, DINING_CHAIR_RAIL_SIZE],
    ["left-rail", -width * 0.36, 0, DINING_CHAIR_RAIL_SIZE, depth * 0.6],
    ["right-rail", width * 0.36, 0, DINING_CHAIR_RAIL_SIZE, depth * 0.6],
  ] as const;
  const chairBox = (
    name: string,
    localX: number,
    localY: number,
    z: number,
    size: [number, number, number],
  ) => {
    const [offsetX, offsetY] = rotateOffset(localX, localY, rotationZ);
    return box(
      `chair-${id}-${name}`,
      [x + offsetX, y + offsetY, z],
      size,
      "furnitureMetal",
      false,
      rotationZ,
      Math.min(DINING_CHAIR_RAIL_SIZE, MIN_SIZE),
    );
  };

  return [
    ...legOffsets.map(([localX, localY], index) => {
      const [offsetX, offsetY] = rotateOffset(localX, localY, rotationZ);
      return box(
        `chair-${id}-leg-${legNames[index]}`,
        [x + offsetX, y + offsetY, legZ],
        [legSize, legSize, legHeight],
        "furnitureMetal",
        false,
        rotationZ,
        legSize,
      );
    }),
    ...railCenters.map(([name, localX, localY, railWidth, railDepth]) => {
      return chairBox(name, localX, localY, railZ, [
        railWidth,
        railDepth,
        DINING_CHAIR_RAIL_SIZE,
      ]);
    }),
    chairBox("seat", 0, 0, seatZ, [width, depth, seatHeight]),
    chairBox("front-apron", 0, -depth * 0.43, seatZ - seatHeight * 0.72, [
      width * 0.9,
      DINING_CHAIR_RAIL_SIZE,
      seatHeight * 0.75,
    ]),
    chairBox("back", 0, backLocalY, backZ, [
      width * 0.72,
      backThickness,
      backHeight * 0.86,
    ]),
    chairBox("back-post-left", -backPostX, backLocalY, postZ, [
      backPostWidth,
      backThickness * 1.12,
      postHeight,
    ]),
    chairBox("back-post-right", backPostX, backLocalY, postZ, [
      backPostWidth,
      backThickness * 1.12,
      postHeight,
    ]),
    chairBox("crest", 0, backLocalY + backThickness * 0.08, crestZ, [
      width * 0.98,
      backThickness * 1.18,
      backHeight * 0.12,
    ]),
    chairBox("lower-back-bar", 0, backLocalY, lowerBackBarZ, [
      width * 0.86,
      backThickness * 1.08,
      backHeight * 0.08,
    ]),
  ];
}

function roundedDiningChairPart(
  id: string,
  centerX: number,
  centerY: number,
  z: number,
  width: number,
  depth: number,
  height: number,
  radius: number,
): LivingRoom3DPart {
  return extrudedShape(
    id,
    createRoundedRectOutline(centerX, centerY, width, depth, radius),
    z,
    height,
    "furnitureMetal",
    false,
  );
}

function getDiningTableSurfaceHeight(height: number): number {
  return Math.max(
    height -
      (DINING_TABLE_DEFAULT_MODEL_HEIGHT - DINING_TABLE_SURFACE_HEIGHT),
    MIN_SIZE,
  );
}

function getDiningTabletopThickness(height: number): number {
  return clamp(height * 0.08, 0.05, 0.065);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function rotateOffset(
  x: number,
  y: number,
  rotationZ: number,
): [number, number] {
  const cos = Math.cos(rotationZ);
  const sin = Math.sin(rotationZ);
  return [x * cos - y * sin, x * sin + y * cos];
}

function getFootprint(symbol: SymbolType, modelKey: PrimitiveSymbol3DModelKey) {
  const defaults = getPrimitiveSymbol3DDefaults(modelKey);
  const width = isSvgType(symbol)
    ? symbol.svgLength
    : isPolygonSymbolType(symbol)
      ? symbol.polygonWidth
      : (symbol.threeD?.dimensions?.width ?? defaults?.dimensions.width ?? 1);
  const depth = isSvgType(symbol)
    ? symbol.svgHeight
    : isPolygonSymbolType(symbol)
      ? symbol.polygonHeight
      : (symbol.threeD?.dimensions?.depth ?? defaults?.dimensions.depth ?? 1);

  return {
    width: Math.max(width, MIN_SIZE),
    depth: Math.max(depth, MIN_SIZE),
  };
}

function getHeight(symbol: SymbolType, modelKey: PrimitiveSymbol3DModelKey) {
  const minimumHeight = getMinimumHeight(modelKey);
  const explicitHeight = symbol.threeD?.height;
  if (
    Number.isFinite(explicitHeight) &&
    typeof explicitHeight === "number" &&
    explicitHeight > 0
  ) {
    return Math.max(explicitHeight, minimumHeight);
  }
  return Math.max(
    getPrimitiveSymbol3DDefaults(modelKey)?.height ?? 0.7,
    minimumHeight,
  );
}

function getMinimumHeight(modelKey: PrimitiveSymbol3DModelKey): number {
  return isPolygonCoffeeTable(modelKey) ? POLYGON_COFFEE_TABLE_MIN_HEIGHT : 0;
}

function isPolygonCoffeeTable(modelKey: PrimitiveSymbol3DModelKey): boolean {
  return (
    modelKey === "living:coffee-table-octagonal" ||
    modelKey === "living:coffee-table-pentagonal" ||
    modelKey === "living:coffee-table-hexagonal"
  );
}

function getBaseElevation(
  symbol: SymbolType,
  modelKey: PrimitiveSymbol3DModelKey,
) {
  const baseElevation = symbol.threeD?.baseElevation;
  return Number.isFinite(baseElevation) && typeof baseElevation === "number"
    ? baseElevation
    : (getPrimitiveSymbol3DDefaults(modelKey)?.baseElevation ?? 0);
}

function pillow(
  id: string,
  center: [number, number, number],
  size: [number, number, number],
  materialSlot: FloorPlan3DMaterialSlot,
  options: {
    segments?: [number, number, number];
    shape?: PillowShapeOptions;
    rotationX?: number;
    rotationY?: number;
    rotationZ?: number;
    roughness?: number;
    useFillColor?: boolean;
  } = {},
): LivingRoom3DPart {
  return {
    id,
    type: "pillow",
    center,
    size: size.map((value) => Math.max(value, MIN_SIZE)) as [
      number,
      number,
      number,
    ],
    segments: options.segments,
    shape: options.shape,
    materialSlot,
    useFillColor: options.useFillColor ?? false,
    rotationX: options.rotationX,
    rotationY: options.rotationY,
    rotationZ: options.rotationZ,
    roughness: options.roughness,
  };
}

function box(
  id: string,
  center: [number, number, number],
  size: [number, number, number],
  materialSlot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  rotationZ?: number,
  minSize = MIN_SIZE,
): Extract<LivingRoom3DPart, { type: "box" }> {
  return {
    id,
    type: "box",
    center,
    size: size.map((value) => Math.max(value, minSize)) as [
      number,
      number,
      number,
    ],
    materialSlot,
    useFillColor,
    rotationZ,
  };
}

function cylinder(
  id: string,
  center: [number, number, number],
  height: number,
  radiusX: number,
  radiusY: number,
  radialSegments: number,
  materialSlot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  rotationZ?: number,
  minSize = MIN_SIZE,
): Extract<LivingRoom3DPart, { type: "cylinder" }> {
  return {
    id,
    type: "cylinder",
    center,
    height: Math.max(height, minSize),
    radiusX: Math.max(radiusX, minSize),
    radiusY: Math.max(radiusY, minSize),
    radialSegments,
    materialSlot,
    useFillColor,
    rotationZ,
  };
}

function ellipsoid(
  id: string,
  center: [number, number, number],
  radiusX: number,
  radiusY: number,
  radiusZ: number,
  widthSegments: number,
  heightSegments: number,
  materialSlot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  minSize = MIN_SIZE,
): LivingRoom3DPart {
  return {
    id,
    type: "ellipsoid",
    center,
    radiusX: Math.max(radiusX, minSize),
    radiusY: Math.max(radiusY, minSize),
    radiusZ: Math.max(radiusZ, minSize),
    widthSegments,
    heightSegments,
    materialSlot,
    useFillColor,
  };
}

function foliageCylinder(
  id: string,
  center: [number, number, number],
  height: number,
  radiusX: number,
  radiusY: number,
  radialSegments: number,
  materialSlot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  rotationZ?: number,
): LivingRoom3DPart {
  return cylinder(
    id,
    center,
    height,
    radiusX,
    radiusY,
    radialSegments,
    materialSlot,
    useFillColor,
    rotationZ,
    FOLIAGE_MIN_SIZE,
  );
}

function extrudedShape(
  id: string,
  points: [number, number][],
  z: number,
  height: number,
  materialSlot: FloorPlan3DMaterialSlot,
  useFillColor = false,
  rotationZ?: number,
  holes?: [number, number][][],
  topSlopeMaxDrop?: number,
  topSlopeOuterRadius?: number,
  topSlopeInnerRadius?: number,
): Extract<LivingRoom3DPart, { type: "extrudedShape" }> {
  return {
    id,
    type: "extrudedShape",
    center: [0, 0, z],
    points,
    holes,
    z,
    height: Math.max(height, MIN_SIZE),
    materialSlot,
    useFillColor,
    rotationZ,
    topSlopeMaxDrop,
    topSlopeOuterRadius,
    topSlopeInnerRadius,
  };
}

function createSymbolsParts(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  if (modelKey === "symbols:bonnie") {
    return createBonnieParts(width, depth, height, baseElevation);
  }
  if (modelKey === "symbols:curtain") {
    return createCurtainParts(width, depth, height, baseElevation);
  }
  if (modelKey.startsWith("symbols:hangers-")) {
    return createHangersParts(modelKey, width, depth, height, baseElevation);
  }
  if (modelKey === "symbols:lighting" || modelKey === "symbols:spotlight") {
    return createClassicRoundSpotlightParts(
      width,
      depth,
      height,
      baseElevation,
    );
  }
  if (modelKey === "symbols:lighting-fixture") {
    return createLightingFixtureParts(width, depth, height, baseElevation);
  }
  if (modelKey === "symbols:ungrounded-power-outlet") {
    return createUngroundedPowerOutletParts(
      width,
      depth,
      height,
      baseElevation,
    );
  }
  return [];
}

/**
 * A single, recessed Type C wall socket. The faceplate follows the wall-facing
 * edge of the 2D electrical marker while retaining the marker's full footprint.
 */
function createUngroundedPowerOutletParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const detailMinSize = 0.001;
  const plateDiameter = Math.min(width * 0.46, height);
  const plateRadius = plateDiameter / 2;
  const plateDepth = clamp(depth * 0.08, 0.012, 0.018);
  const plateBackY = -depth / 2;
  const plateFrontY = plateBackY + plateDepth;
  const centerZ = baseElevation + height / 2;
  const socketRadius = plateRadius * 0.66;
  const socketFaceRadius = plateRadius * 0.54;
  const socketRecessDepth = Math.min(plateDepth * 0.22, 0.003);
  const socketFaceDepth = Math.min(plateDepth * 0.16, 0.002);
  const pinHoleDepth = 0.0015;
  const pinHoleRadius = plateRadius * 0.072;
  const pinOffset = plateRadius * 0.23;

  const wallFacingCylinder = (
    id: string,
    y: number,
    cylinderDepth: number,
    radius: number,
    materialSlot: FloorPlan3DMaterialSlot,
  ): Extract<LivingRoom3DPart, { type: "cylinder" }> => ({
    id,
    type: "cylinder",
    center: [0, y, centerZ],
    height: Math.max(cylinderDepth, detailMinSize),
    radiusX: Math.max(radius, detailMinSize),
    radiusY: Math.max(radius, detailMinSize),
    radialSegments: 32,
    materialSlot,
    useFillColor: false,
    rotationX: -Math.PI / 2,
  });

  const socketFaceY = plateFrontY + socketRecessDepth + socketFaceDepth / 2;
  const pinHoleY = socketFaceY + socketFaceDepth / 2 + pinHoleDepth / 2;

  return [
    wallFacingCylinder(
      "faceplate",
      plateBackY + plateDepth / 2,
      plateDepth,
      plateRadius,
      "doorPanel",
    ),
    wallFacingCylinder(
      "socket-recess",
      plateFrontY + socketRecessDepth / 2,
      socketRecessDepth,
      socketRadius,
      "doorFrame",
    ),
    wallFacingCylinder(
      "socket-face",
      socketFaceY,
      socketFaceDepth,
      socketFaceRadius,
      "doorPanel",
    ),
    {
      ...wallFacingCylinder(
        "pin-hole-left",
        pinHoleY,
        pinHoleDepth,
        pinHoleRadius,
        "furnitureScreen",
      ),
      center: [-pinOffset, pinHoleY, centerZ],
    },
    {
      ...wallFacingCylinder(
        "pin-hole-right",
        pinHoleY,
        pinHoleDepth,
        pinHoleRadius,
        "furnitureScreen",
      ),
      center: [pinOffset, pinHoleY, centerZ],
    },
  ];
}

function createCurtainParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const rodRadius = 0.015;
  const rodHeight = height - rodRadius;
  return [
    box(
      "rod",
      [0, 0, baseElevation + rodHeight],
      [width, 0.03, 0.03],
      "furnitureMetal",
    ),
    extrudedShape(
      "fabric",
      createCurtainOutline(width),
      baseElevation + height * 0.02,
      height * 0.94,
      "furnitureFabric",
      false,
    ),
  ];
}

function createCurtainOutline(
  width: number,
  frequency = 8,
  amplitude = 0.028,
  thickness = 0.006,
): [number, number][] {
  const points: [number, number][] = [];
  const segments = 64;
  const halfWidth = width / 2;

  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    const x = -halfWidth + t * width;
    const y = Math.sin(t * Math.PI * 2 * frequency) * amplitude;
    points.push([x, y]);
  }

  for (let i = segments; i >= 0; i -= 1) {
    const t = i / segments;
    const x = -halfWidth + t * width;
    const y = Math.sin(t * Math.PI * 2 * frequency) * amplitude - thickness;
    points.push([x, y]);
  }

  return points;
}

/** Shared round spotlight used by both current and legacy lighting symbols. */
function createClassicRoundSpotlightParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const diameter = Math.min(width, depth);
  const headHeight = Math.min(height, diameter * 0.75);
  const headTopZ = baseElevation + headHeight;
  const fixtureTopZ = baseElevation + height;
  const detailMinSize = Math.min(diameter, headHeight) * 0.01;
  const frontAssemblyHeight = headHeight * 0.18;
  const mainHousingHeight = headHeight * 0.48;
  const rearHousingHeight =
    headHeight - frontAssemblyHeight - mainHousingHeight;
  const housingTransitionZ =
    baseElevation + frontAssemblyHeight + mainHousingHeight;
  const mountingPlateHeight = Math.min(diameter * 0.08, height * 0.1);
  const mountingPlateBottomZ = fixtureTopZ - mountingPlateHeight;
  const suspensionStemHeight = mountingPlateBottomZ - headTopZ;
  const parts: LivingRoom3DPart[] = [
    cylinder(
      "mounting-plate",
      [0, 0, fixtureTopZ - mountingPlateHeight / 2],
      mountingPlateHeight,
      diameter * 0.45,
      diameter * 0.45,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "rear-housing",
      [0, 0, housingTransitionZ + rearHousingHeight / 2],
      rearHousingHeight,
      diameter * 0.32,
      diameter * 0.32,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "housing",
      [0, 0, baseElevation + frontAssemblyHeight + mainHousingHeight / 2],
      mainHousingHeight,
      diameter * 0.4,
      diameter * 0.4,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "cooling-ring",
      [0, 0, housingTransitionZ],
      headHeight * 0.055,
      diameter * 0.42,
      diameter * 0.42,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "bezel",
      [
        0,
        0,
        baseElevation +
          frontAssemblyHeight * 0.2 +
          (frontAssemblyHeight * 0.72) / 2,
      ],
      frontAssemblyHeight * 0.72,
      diameter * 0.5,
      diameter * 0.5,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "reflector",
      [
        0,
        0,
        baseElevation +
          frontAssemblyHeight * 0.1 +
          (frontAssemblyHeight * 0.32) / 2,
      ],
      frontAssemblyHeight * 0.32,
      diameter * 0.41,
      diameter * 0.41,
      32,
      "furnitureGlass",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "lens",
      [0, 0, baseElevation + (frontAssemblyHeight * 0.18) / 2],
      frontAssemblyHeight * 0.18,
      diameter * 0.34,
      diameter * 0.34,
      32,
      "furnitureGlass",
      false,
      undefined,
      detailMinSize,
    ),
  ];

  if (suspensionStemHeight > detailMinSize) {
    parts.splice(
      1,
      0,
      cylinder(
        "suspension-stem",
        [0, 0, headTopZ + suspensionStemHeight / 2],
        suspensionStemHeight,
        diameter * 0.045,
        diameter * 0.045,
        16,
        "furnitureMetal",
        false,
        undefined,
        detailMinSize,
      ),
    );
  }

  return parts;
}

function createLightingFixtureParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const detailMinSize = 0.004;
  const finialHeight = height * 0.12;
  const domeRadiusZ = height * 0.36;
  const domeTopZ = baseElevation + height * 0.8;
  const domeCenterZ = domeTopZ - domeRadiusZ;
  const rimHeight = height * 0.1;
  const rimCenterZ = domeTopZ - rimHeight / 2;
  const collarHeight = height * 0.08;
  const collarCenterZ = domeTopZ + collarHeight / 2;
  const canopyHeight = height * 0.16;

  return [
    cylinder(
      "ceiling-canopy",
      [0, 0, baseElevation + height - canopyHeight / 2],
      canopyHeight,
      width * 0.28,
      depth * 0.28,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "canopy-collar",
      [0, 0, collarCenterZ],
      collarHeight,
      width * 0.36,
      depth * 0.36,
      32,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    cylinder(
      "shade-rim",
      [0, 0, rimCenterZ],
      rimHeight,
      width * 0.5,
      depth * 0.5,
      40,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
    ellipsoid(
      "glass-dome",
      [0, 0, domeCenterZ],
      width * 0.44,
      depth * 0.44,
      domeRadiusZ,
      40,
      20,
      "furnitureGlass",
      false,
      detailMinSize,
    ),
    cylinder(
      "shade-finial",
      [0, 0, baseElevation + finialHeight / 2],
      finialHeight,
      Math.min(width, depth) * 0.055,
      Math.min(width, depth) * 0.055,
      24,
      "furnitureMetal",
      false,
      undefined,
      detailMinSize,
    ),
  ];
}
